// API Service for connecting to FastAPI backend
// This service handles all API calls to the backend without touching the UI

import { VIEW_AS_HEADER, isViewAsTab, liveViewAs, markViewAsEnded, viewAs } from './viewAsState';
import type {
  OnboardingPublicConfig, PicksResponse, PicksQuery, OnboardingSavePayload, OnboardingEventBody,
} from '../types/onboarding';

/**
 * The endpoint the website's main search (Direct Search, typed onboarding
 * keywords) POSTs to. This is the ONE line that decides whether the site runs
 * on the new search_v2 orchestrator or on the old hybrid path: set it to
 * LEGACY_SEARCH_ENDPOINT to flip the whole site back.
 *
 * search_v2 returns the same envelope and hit shape as the legacy endpoint
 * plus a `meta` block (route/engine/timings), so nothing downstream changes.
 * Which engine v2 actually runs is an admin setting (System → Search engine
 * (v2)), not a frontend concern.
 */
export const SEARCH_ENDPOINT: string = '/search/v2';
/** The pre-v2 main search path, kept so the flip back is a constant swap. */
export const LEGACY_SEARCH_ENDPOINT: string = '/api/v1/direct-search/hybrid';

export interface ApiResponse<T = any> {
  success: boolean;
  data?: T;
  error?: string;
  status?: number;
  // Additional fields for invite requests
  user_exists?: boolean;
  action?: string;
  message?: string;
  email_sent?: boolean;
  invite_id?: string;
}

export interface ConversationResponse {
  type: 'followup' | 'search_results' | 'out_of_scope';
  question?: string; // For followup responses
  reasoning?: string;
  session_id: string;
  user_id?: string | null;
  user_message_id?: string;
  ai_message_id?: string;
  iteration?: number;
  followups_remaining?: number;
  session_title?: string; // Auto-generated title
  session_updated?: boolean;
  query?: string; // Compiled query used for search
  expanded_query?: string;
  summary?: string; // AI-generated summary for search results
  results?: any[]; // Search results
  processing_time?: number;
  session_reset?: boolean;
  // Out of scope response fields
  response?: string; // Direct response for out of scope queries
  confidence_score?: number; // Confidence score for scope detection
  extensive_search?: boolean; // Whether extensive search was used
}

// Streaming status types
export type StreamStatus = 
  | 'thinking' 
  | 'scoping' 
  | 'broadening' 
  | 'applying_filters' 
  | 'scoring' 
  | 'compiling' 
  | 'complete' 
  | 'error' 
  | 'followup' 
  | 'out_of_scope' 
  | 'no_results'
  | 'context_break'
  | 'broad_query_detected'
  | 'action_suggestion';

export interface StreamingUpdate {
  status: StreamStatus;
  message: string;
  results_count: number;
  timestamp: string;
  data?: ConversationResponse;
}

export interface EmailCheckResponse {
  exists: boolean;
  invite_status?: string | null;
  /** The address was retired by an admin e-mail change; `message` says what to do. */
  moved?: boolean;
  message?: string | null;
  /** Only for an address with no account: a Google / LinkedIn sign-in makes
   *  the account straight away (true) or asks first (admin → Users → Sign-in). */
  oauth_auto_create?: boolean | null;
}

export interface OTPSendResponse {
  otp_id: string;
  ttl_sec: number;
}

export interface OTPVerifyResponse {
  access_token: string;
  refresh_token: string;
  user_id: string;
  first_login: boolean;
  /** purpose=password_reset only: one-time token for confirmPasswordReset. */
  reset_token?: string | null;
}

export interface ClerkAuthResponse {
  access_token: string;
  refresh_token: string;
  user_id: string;
  first_login: boolean;
  email: string;
}

export interface ProfileResponse {
  user: {
    user_id: string;
    email: string;
    first_name: string;
    last_name: string;
    phone: string;
    city: string;
    country: string;
    profile_picture_url?: string;
    has_password?: boolean;
  };
  profile: {
    organisation_name?: string;
    designation?: string;
    linkedin_profile_url?: string;
    highest_qualification?: string;
    professional_summary?: string;
    company_website_url?: string;
    hats?: string[];
    intent?: string[];
    collaboration_types?: string[];
    collaboration_lead?: string[];
    user_roles?: string[];
    collaboration_timeline?: string;
    rd_budget?: string;
    rd_summary?: string;
    other_info?: string;
    interests?: string[];
    alerts_enabled?: boolean;
    last_notified_at?: string;
  };
}

export interface AuthStatusResponse {
  profile_completion: {
    personal: number;
    professional: number;
    rd: number;
    interests: number;
    overall: number;
    score?: number;
    max?: number;
    percentage?: number;
    contact_ready?: boolean;
    summary_ready?: boolean;
  };
  next_screen: string;
}

/** A suspended account (Backend/account_status.py): signed-in routes, bare
 *  user_id routes, sign-in routes and token refresh answer 403 with this
 *  detail. Outside sign-in, ApiService clears the stored session and fires
 *  ACCOUNT_DISABLED_EVENT on window; index.tsx signs out (Clerk too) and says
 *  why. A sign-in form shows the same sentence itself (utils/authErrors). */
export const ACCOUNT_DISABLED = 'account_disabled';
export const ACCOUNT_DISABLED_EVENT = 'utech:account-disabled';
/** The sign-in is over (owner, 2026-10-28: members kept seeing "Session
 *  expired or invalid — please sign in again" on a page that went on looking
 *  signed in). A signed-in call answered 401 and renewing the token did not
 *  help (the refresh token is dead or revoked, or there is none): ApiService
 *  clears the stored session and fires SESSION_EXPIRED_EVENT once; index.tsx
 *  signs out (Clerk too) and the home page opens the sign-in modal, told why
 *  by SESSION_EXPIRED_FLAG (sessionStorage, as the sign-out reloads the page). */
export const SESSION_EXPIRED_EVENT = 'utech:session-expired';
export const SESSION_EXPIRED_FLAG = 'utech_session_expired';
/** Every billing summary that loads, wherever it was asked for (the Billing
 *  tab, the checkout's own re-checks), as a CustomEvent with detail
 *  {userId, data}. App keeps the upgrade prompts in step with it: on
 *  2026-10-28 a member paid, the payment confirmation hit a moment the API
 *  was restarting, and the page that had loaded them as Explorer kept
 *  showing "Upgrade to Voyager" for hours while their Billing tab, which
 *  fetched its own summary, showed the membership. */
export const BILLING_SUMMARY_EVENT = 'utech:billing-summary';
/** A page behind feature access (Backend/feature_access.py: the new For You,
 *  What's New, OCEAN) refused this account: 403 with this detail. It means the
 *  tab's feature access is out of date (e.g. the feature went back to "admins"
 *  while the page was open). makeRequest fires FEATURE_NOT_ENABLED_EVENT on
 *  window — utils/featureAccess.ts re-asks and App swaps the page for its
 *  fallback — and shows FEATURE_NOT_ENABLED_TEXT instead of the code. */
export const FEATURE_NOT_ENABLED = 'feature_not_enabled';
export const FEATURE_NOT_ENABLED_EVENT = 'utech:feature-not-enabled';
export const FEATURE_NOT_ENABLED_TEXT = "This page isn't available for your account right now. Please reload the page.";
/** An admin-panel route behind a group or sub-tab this admin has not been
 *  given (Backend/admin_access.py): 403 with this detail. makeRequest fires
 *  ADMIN_ACCESS_CHANGED_EVENT on window so the admin shell re-reads its
 *  access, and shows ADMIN_TAB_NOT_ENABLED_TEXT instead of the code. */
export const ADMIN_TAB_NOT_ENABLED = 'admin_tab_not_enabled';
export const ADMIN_TAB_NOT_ENABLED_TEXT = "This admin page isn't enabled for your account.";
export const ADMIN_ACCESS_CHANGED_EVENT = 'utech:admin-access-changed';
const SIGN_IN_PATHS = ['/api/v1/auth/otp/verify', '/api/v1/auth/password/login',
  '/api/v1/auth/clerk/login', '/api/v1/auth/signup'];

class ApiService {
  private baseUrl: string;

  constructor() {
    // Use environment variable for API URL, fallback to AWS EC2 endpoint
    this.baseUrl = process.env.VITE_API_BASE_URL || 'https://api.universitytech.io';
    
    // Log the API URL for debugging
    console.log('API Base URL:', this.baseUrl);
  }

  /**
   * Normalise a FastAPI error body into a real string.
   *
   * `detail` is not always a string:
   *   HTTPException(detail="...")  -> string
   *   validation error (422)       -> [{ loc, msg, type, input }, ...]
   *   some handlers                -> { message: "..." }
   *
   * Interpolating the 422 array into a template literal is what showed users
   * "Your message couldn't be sent: [object Object]". Everything assigned to
   * ApiResponse.error goes through here so the declared `error?: string` is true.
   *
   * Never surface pydantic's `input` -- it echoes back what was submitted.
   */
  /** Origin the connection-speed probe should ping (see utils/connectionCheck). */
  getApiBaseUrl(): string {
    return this.baseUrl;
  }

  private normalizeApiError(detail: any, status: number): string {
    const fallback = `HTTP error! status: ${status}`;
    if (detail === null || detail === undefined) return fallback;
    if (detail === FEATURE_NOT_ENABLED) return FEATURE_NOT_ENABLED_TEXT;
    if (detail === ADMIN_TAB_NOT_ENABLED) return ADMIN_TAB_NOT_ENABLED_TEXT;
    if (detail === 'read_only_view') return 'Read-only view: nothing is saved while you view as this member.';
    if (detail === 'read_only_team') return 'Your team account is read-only: you can look at everything you can open, but not change it.';
    if (detail === 'not_in_view_as') return 'Not available while viewing as a member.';
    if (detail === 'view_as_ended') return 'This View as member has ended.';
    if (typeof detail === 'string') return detail.trim() || fallback;

    if (Array.isArray(detail)) {
      const parts = detail
        .map((item: any) => {
          if (typeof item === 'string') return item;
          if (!item || typeof item !== 'object') return '';
          const msg = item.msg || item.message || '';
          if (!msg) return '';
          // loc looks like ["body", "cc_email"] - drop the location prefix
          const field = Array.isArray(item.loc)
            ? item.loc.filter((p: any) => p !== 'body' && p !== 'query' && p !== 'path').join('.')
            : '';
          return field ? `${field}: ${msg}` : String(msg);
        })
        .filter(Boolean);
      return parts.length ? parts.join('; ') : fallback;
    }

    if (typeof detail === 'object') {
      return detail.msg || detail.message || detail.error || fallback;
    }
    return String(detail) || fallback;
  }

  /** ONE token renewal per tab. App mount alone fires several bearer calls at
   *  once; without this each would start its own refresh, and the backend
   *  OVERWRITES user_sessions.access_token in place — so every extra refresh
   *  invalidates the token the previous one just issued. */
  private static refreshInFlight: Promise<string | null> | null = null;
  /** The last renewal was REFUSED (401: the refresh token is dead or revoked),
   *  as opposed to failing on the network or a 5xx — only a refusal means the
   *  session is over (signalSessionExpired). */
  private static refreshRefused = false;

  /** Renew the access token from the stored 30-day refresh token.
   *
   *  Deliberately raw fetch, never makeRequest: the refresh endpoint itself
   *  answers 401 for a dead token, so routing it through the retry path below
   *  would recurse forever.
   */
  private refreshAccessToken(): Promise<string | null> {
    if (ApiService.refreshInFlight) return ApiService.refreshInFlight;
    const refreshToken = this.getStoredRefreshToken();
    if (!refreshToken) return Promise.resolve(null);
    ApiService.refreshRefused = false;          // this attempt's answer decides
    ApiService.refreshInFlight = fetch(`${this.baseUrl}/api/v1/auth/token/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ refresh_token: refreshToken }),
      mode: 'cors',
      credentials: 'omit',
    })
      .then(async r => {
        ApiService.refreshRefused = r.status === 401;
        if (!r.ok) {
          // Suspended: refused with 403 account_disabled rather than 401.
          if (r.status === 403) {
            const d = await r.json().catch(() => null as any);
            if (d?.detail === ACCOUNT_DISABLED) this.signalAccountDisabled();
          }
          return null;                          // dead or revoked; sign-in required
        }
        const d = await r.json().catch(() => null as any);
        const token = d?.access_token || null;  // the response carries only this
        if (token) this.setStoredAccessToken(token);
        return token;
      })
      .catch(() => null)
      .finally(() => { ApiService.refreshInFlight = null; });
    return ApiService.refreshInFlight;
  }

  /** The account was suspended: drop the stored session and tell the app.
   *  Fires once per session — the first refused call clears it, so the rest
   *  of a burst (several calls in flight at once) finds nothing to sign out. */
  private signalAccountDisabled(): void {
    const hadSession = !!(this.getStoredUserId() || this.getStoredAccessToken()
      || this.getStoredRefreshToken());
    this.clearStoredAuth();
    if (!hadSession) return;
    try { window.dispatchEvent(new Event(ACCOUNT_DISABLED_EVENT)); } catch { /* not in a browser */ }
  }

  /** A signed-in call was refused for good (see SESSION_EXPIRED_EVENT). Fires
   *  once per session — the first refusal clears it, so the rest of a burst of
   *  calls finds nothing to sign out. */
  private signalSessionExpired(): void {
    const hadSession = !!(this.getStoredUserId() || this.getStoredAccessToken()
      || this.getStoredRefreshToken());
    this.clearStoredAuth();
    if (!hadSession) return;
    try { window.dispatchEvent(new Event(SESSION_EXPIRED_EVENT)); } catch { /* not in a browser */ }
  }

  private async makeRequest<T>(
    endpoint: string,
    options: RequestInit = {},
    // Set only by the 401 path below, so a request is retried exactly once.
    isRetry = false
  ): Promise<ApiResponse<T>> {
    try {
      // Update activity timestamp on each API request (user is active)
      this.updateLastActivity();
      
      const url = `${this.baseUrl}${endpoint}`;
      
      // Log the request for debugging
      // Method and URL only. The options carry request bodies (sign-in
      // passwords, OTPs, form fields) and the bearer token in headers, and the
      // console is captured by PostHog session recording — never log them.
      console.log('Making API request to:', (options.method || 'GET').toUpperCase(), url);
      
      const response = await fetch(url, {
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          ...options.headers,
        },
        ...options,
        // Add mode and credentials for CORS
        mode: 'cors',
        credentials: 'omit',
      });

      console.log('API Response status:', response.status, response.statusText);

      // Not every error body is JSON - an nginx/ALB 502 returns HTML, and letting
      // .json() throw here would surface a SyntaxError as a "network error".
      const data = await response.json().catch(() => ({} as any));

      if (!response.ok) {
        // Suspended mid-session (or on reload): sign the tab out and let
        // index.tsx say why. A sign-in call is left to the form that made it,
        // which shows the same sentence — a second notice would only stack.
        if (response.status === 403 && data?.detail === ACCOUNT_DISABLED
            && !SIGN_IN_PATHS.some(p => endpoint.startsWith(p))) {
          this.signalAccountDisabled();
        }
        if (response.status === 403 && data?.detail === FEATURE_NOT_ENABLED) {
          try { window.dispatchEvent(new Event(FEATURE_NOT_ENABLED_EVENT)); } catch { /* not in a browser */ }
        }
        if (response.status === 403 && data?.detail === ADMIN_TAB_NOT_ENABLED) {
          try { window.dispatchEvent(new Event(ADMIN_ACCESS_CHANGED_EVENT)); } catch { /* not in a browser */ }
        }
        // The access token expires on an ABSOLUTE clock while the UI's idle
        // timer is sliding, so an actively-working user's token can die
        // mid-session. Nothing renewed it, so billing and entitlement calls
        // simply began failing — which is how a paying subscriber ended up
        // being shown an upgrade paywall. Renew once and replay the request.
        //
        // Auth endpoints are excluded: a 401 from sign-in means bad
        // credentials, not an expired session, and retrying is pointless.
        // Changing a password is the exception: it needs the session, so a
        // 401 there is an expired token like anywhere else.
        const isAuthCall = endpoint.includes('/auth/') && !endpoint.includes('/auth/password/change');
        if (response.status === 401 && !isRetry && !isAuthCall
            && this.getStoredRefreshToken()) {
          const hdrs = { ...(options.headers as Record<string, string> | undefined) };
          const authKey = Object.keys(hdrs).find(k => k.toLowerCase() === 'authorization');
          // Another call may have renewed the session while this one was in
          // flight: its 401 is for the token that renewal replaced. Renewing
          // again would replace the new token too (one access token per
          // session) and fail every call that joined the first renewal, so
          // retry with the stored one instead.
          const stored = this.getStoredAccessToken();
          const renewedMeanwhile = !!(authKey && stored && hdrs[authKey] !== `Bearer ${stored}`);
          const fresh = renewedMeanwhile ? stored : await this.refreshAccessToken();
          if (fresh) {
            // Re-stamp the bearer header if the caller sent one; the value it
            // built is the token we just replaced.
            const retryOptions: RequestInit = { ...options };
            if (authKey) hdrs[authKey] = `Bearer ${fresh}`;
            retryOptions.headers = hdrs;
            return this.makeRequest<T>(endpoint, retryOptions, true);
          }
        }
        // Still 401 with the session's own token, after the renewal above
        // failed or was not possible: the session is over. Sign out rather
        // than leave the page showing "please sign in again" while it still
        // looks signed in. Only for calls that carried a bearer token: a
        // signed-out visitor's 401 (a public page's locked action) is theirs.
        if (response.status === 401 && !isAuthCall) {
          const sentBearer = Object.entries((options.headers as Record<string, string> | undefined) || {})
            .some(([k, v]) => k.toLowerCase() === 'authorization' && /^bearer\s+\S/i.test(String(v || '')));
          // Over = there is no refresh token to renew with, or the renewal
          // itself was refused. Not a refused retry on its own: two tabs
          // renewing at once replace each other's access token (one per
          // session) while the refresh token stays good, so the next call
          // renews again. A renewal that failed on the network leaves the
          // session alone too.
          const over = !this.getStoredRefreshToken() || ApiService.refreshRefused;
          if (sentBearer && over) this.signalSessionExpired();
        }
        console.error('API Error:', response.status, response.statusText);
        return {
          success: false,
          error: this.normalizeApiError(data?.detail, response.status),
          status: response.status,
        };
      }

      // No body: responses carry access/refresh tokens (sign-in), profiles and
      // e-mails, and the console is captured by PostHog session recording.
      return {
        success: true,
        data,
        status: response.status,
      };
    } catch (error) {
      console.error('API request failed:', error);
      
      // More specific error handling for network issues
      if (error instanceof TypeError && error.message.includes('fetch')) {
        return {
          success: false,
          error: 'Network error: Unable to connect to API server. Please check if the API server is running and accessible.',
        };
      }
      
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Network error',
      };
    }
  }

  // // 1. Chat/Conversation API
  // async processConversation(
  //   userInput: string,
  //   sessionId?: string | null,
  //   userId?: string | null,
  //   options: {
  //     enableFollowups?: boolean;
  //     maxFollowups?: number;
  //     searchType?: string;
  //     topN?: number;
  //     scoreThreshold?: number;
  //     enableQueryExpansion?: boolean;
  //   } = {}
  // ): Promise<ApiResponse<ConversationResponse>> {
  //   const body = {
  //     user_input: userInput,
  //     session_id: sessionId,
  //     user_id: userId,
  //     enable_followups: options.enableFollowups ?? true,
  //     max_followups: options.maxFollowups ?? 10,
  //     search_type: options.searchType ?? 'Both',
  //     top_n: options.topN ?? 10,
  //     score_threshold: options.scoreThreshold ?? 0.4,
  //     enable_query_expansion: options.enableQueryExpansion ?? true,
  //   };

  //   console.log('🔄 Calling V1 endpoint: /api/v1/conversation/process');
  //   return this.makeRequest<ConversationResponse>('/api/v1/conversation/process', {
  //     method: 'POST',
  //     body: JSON.stringify(body),
  //   });
  // }

  async processConversationV2(
    userInput: string,
    sessionId?: string | null,
    userId?: string | null,
    files?: File[],
    options: {
      enableFollowups?: boolean;
      maxFollowups?: number;
      searchType?: string;
      topN?: number;
      scoreThreshold?: number;
      enableQueryExpansion?: boolean;
      extensiveSearch?: boolean;
    } = {}
  ): Promise<ApiResponse<ConversationResponse>> {
    // Process files if provided
    let processedFiles: any[] = [];
    if (files && files.length > 0) {
      console.log(`📎 Processing ${files.length} files for API`);
      
      for (const file of files) {
        try {
          // Convert file to base64
          const base64Content = await new Promise<string>((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => {
              const result = reader.result as string;
              // Remove data URL prefix to get pure base64
              const base64 = result.split(',')[1];
              resolve(base64);
            };
            reader.onerror = reject;
            reader.readAsDataURL(file);
          });

          processedFiles.push({
            name: file.name,
            type: file.type,
            size: file.size,
            content: base64Content
          });
        } catch (error) {
          console.error(`Error processing file ${file.name}:`, error);
        }
      }
    }

    const body = {
      user_input: userInput,
      session_id: sessionId,
      user_id: userId,
      enable_followups: options.enableFollowups ?? true,
      max_followups: options.maxFollowups ?? 10,
      search_type: options.searchType ?? 'Both',
      top_n: options.topN ?? 10,
      score_threshold: options.scoreThreshold ?? 0.4,
      enable_query_expansion: options.enableQueryExpansion ?? true,
      extensive_search: options.extensiveSearch ?? false,
      files: processedFiles.length > 0 ? processedFiles : null,
    };

    console.log('🚀 Calling V2 endpoint: /api/v1/conversation/process/v2');
    console.log(`📎 Sending ${processedFiles.length} processed files`);
    return this.makeRequest<ConversationResponse>('/api/v1/conversation/process/v2', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  /**
   * Process conversation with streaming status updates via Server-Sent Events (SSE)
   * @param onStatusUpdate Callback for each status update
   * @returns Final conversation response
   */
  async processConversationV2Stream(
    userInput: string,
    sessionId?: string | null,
    userId?: string | null,
    files?: File[],
    options: {
      enableFollowups?: boolean;
      maxFollowups?: number;
      searchType?: string;
      topN?: number;
      scoreThreshold?: number;
      enableQueryExpansion?: boolean;
      extensiveSearch?: boolean;
      // When true, route this request to the Algolia-backed V2 stream
      // endpoint instead of the default LanceDB one. The caller decides
      // (e.g. based on the logged-in email) — see utils/featureFlags.ts.
      useAlgoliaBackend?: boolean;
    } = {},
    onStatusUpdate?: (update: StreamingUpdate) => void
  ): Promise<ApiResponse<ConversationResponse>> {
    // Process files if provided
    let processedFiles: any[] = [];
    if (files && files.length > 0) {
      console.log(`📎 Processing ${files.length} files for streaming API`);
      
      for (const file of files) {
        try {
          const base64Content = await new Promise<string>((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => {
              const result = reader.result as string;
              const base64 = result.split(',')[1];
              resolve(base64);
            };
            reader.onerror = reject;
            reader.readAsDataURL(file);
          });

          processedFiles.push({
            name: file.name,
            type: file.type,
            size: file.size,
            content: base64Content
          });
        } catch (error) {
          console.error(`Error processing file ${file.name}:`, error);
        }
      }
    }

    const body = {
      user_input: userInput,
      session_id: sessionId,
      user_id: userId,
      enable_followups: options.enableFollowups ?? true,
      max_followups: options.maxFollowups ?? 10,
      search_type: options.searchType ?? 'Both',
      top_n: options.topN ?? 10,
      score_threshold: options.scoreThreshold ?? 0.4,
      enable_query_expansion: options.enableQueryExpansion ?? true,
      extensive_search: options.extensiveSearch ?? false,
      files: processedFiles.length > 0 ? processedFiles : null,
    };

    // Default everyone to the original LanceDB endpoint; only flagged callers
    // get routed to the Algolia variant. See utils/featureFlags.ts.
    const v2Path = options.useAlgoliaBackend
      ? '/api/v1/conversation/process/v2/stream/algolia'
      : '/api/v1/conversation/process/v2/stream';
    console.log(`🚀 Calling streaming V2 endpoint: ${v2Path}`);

    try {
      const url = `${this.baseUrl}${v2Path}`;
      
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          ...this.billingHeaders(),
          'Accept': 'text/event-stream',
        },
        body: JSON.stringify(body),
        mode: 'cors',
        credentials: 'omit',
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({} as any));
        return {
          success: false,
          error: this.normalizeApiError(errorData?.detail, response.status),
          status: response.status,
        };
      }

      const reader = response.body?.getReader();
      if (!reader) {
        return {
          success: false,
          error: 'Unable to read response stream',
        };
      }

      const decoder = new TextDecoder();
      let finalData: ConversationResponse | null = null;
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || ''; // Keep incomplete line in buffer

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            try {
              const jsonStr = line.slice(6).trim();
              if (!jsonStr) continue;
              
              const update: StreamingUpdate = JSON.parse(jsonStr);
              
              // Call the status update callback
              if (onStatusUpdate) {
                onStatusUpdate(update);
              }

              console.log(`📡 Stream update: ${update.status} - ${update.message} (${update.results_count} results)`);

              // Check for final states
              if (update.status === 'complete' && update.data) {
                finalData = update.data;
              } else if (update.status === 'error') {
                return {
                  success: false,
                  error: update.message,
                };
              } else if (update.status === 'followup' && update.data) {
                finalData = update.data;
              } else if (update.status === 'out_of_scope' && update.data) {
                finalData = update.data;
              } else if (update.status === 'no_results' && update.data) {
                finalData = update.data;
              } else if (update.status === 'context_break' && update.data) {
                finalData = update.data;
              } else if (update.status === 'broad_query_detected' && update.data) {
                finalData = update.data;
              }
            } catch (e) {
              console.warn('Failed to parse SSE data:', line, e);
            }
          }
        }
      }

      if (finalData) {
        return {
          success: true,
          data: finalData,
        };
      }

      return {
        success: false,
        error: 'No final response received from stream',
      };
    } catch (error) {
      console.error('Streaming API request failed:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Network error',
      };
    }
  }

  async testLanceDBSearch(
    query: string,
    topK: number = 5,
    usePrefiltering: boolean = true,
    similarityThreshold?: number
  ): Promise<ApiResponse<any>> {
    const body = {
      query,
      top_k: topK,
      use_prefiltering: usePrefiltering,
      similarity_threshold: similarityThreshold
    };

    return this.makeRequest<any>('/api/v1/conversation/lancedb/test', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  async getLanceDBStats(): Promise<ApiResponse<any>> {
    return this.makeRequest<any>('/api/v1/conversation/lancedb/stats', {
      method: 'GET',
    });
  }

  async searchWeb(
    query: string,
    numResults: number = 5,
    maxResponseLength: number = 500
  ): Promise<ApiResponse<any>> {
    const body = {
      query,
      num_results: numResults,
      max_response_length: maxResponseLength
    };

    return this.makeRequest<any>('/api/v1/search/web', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  async generateComprehensiveSummary(
    userQuery: string,
    techResults: any[] = [],
    webSearchSummary: string = "",
    webSearchSources: any[] = [],
    userProfessionalSummary: string = "",
    userRdSummary: string = "",
    maxChars: number = 1000
  ): Promise<ApiResponse<any>> {
    const body = {
      user_query: userQuery,
      tech_results: techResults,
      web_search_summary: webSearchSummary,
      web_search_sources: webSearchSources,
      user_professional_summary: userProfessionalSummary,
      user_rd_summary: userRdSummary,
      max_chars: maxChars
    };

    return this.makeRequest<any>('/api/v1/llm/comprehensive-summary', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  // 2. Authentication APIs
  async checkEmail(email: string): Promise<ApiResponse<EmailCheckResponse>> {
    return this.makeRequest<EmailCheckResponse>('/api/v1/auth/email/check', {
      method: 'POST',
      body: JSON.stringify({ email }),
    });
  }

  async sendOTP(email: string, purpose: string = 'login'): Promise<ApiResponse<OTPSendResponse>> {
    // The browser asking, so a bounced code can be traced to the person
    // (Admin → Email Center → Delivery trace). Imported lazily: visitorTracking
    // imports this module.
    let ids: { visitor_id?: string; session_id?: string } = {};
    try { ids = (await import('../utils/visitorTracking')).currentIds(); } catch { /* tracking off */ }
    return this.makeRequest<OTPSendResponse>('/api/v1/auth/otp/send', {
      method: 'POST',
      body: JSON.stringify({ email, purpose, ...ids }),
    });
  }

  async verifyOTP(
    email: string, 
    otpCode: string, 
    purpose: string = 'login'
  ): Promise<ApiResponse<OTPVerifyResponse>> {
    return this.makeRequest<OTPVerifyResponse>('/api/v1/auth/otp/verify', {
      method: 'POST',
      body: JSON.stringify({
        email,
        otp_code: otpCode,
        purpose,
      }),
    });
  }

  async resendOTP(otpId: string): Promise<ApiResponse<OTPSendResponse>> {
    return this.makeRequest<OTPSendResponse>('/api/v1/auth/otp/resend', {
      method: 'POST',
      body: JSON.stringify({ otp_id: otpId }),
    });
  }

  async loginWithPassword(
    email: string, 
    password: string, 
    deviceInfo?: string, 
    ip?: string
  ): Promise<ApiResponse<OTPVerifyResponse>> {
    return this.makeRequest<OTPVerifyResponse>('/api/v1/auth/password/login', {
      method: 'POST',
      body: JSON.stringify({
        email,
        password,
        device_info: deviceInfo,
        ip,
      }),
    });
  }

  async loginWithClerk(
    clerkSessionToken: string,
    deviceInfo?: string,
    ip?: string
  ): Promise<ApiResponse<ClerkAuthResponse>> {
    return this.makeRequest<ClerkAuthResponse>('/api/v1/auth/clerk/login', {
      method: 'POST',
      body: JSON.stringify({
        clerk_session_token: clerkSessionToken,
        device_info: deviceInfo,
        ip,
      }),
    });
  }

  async getAuthStatus(userId: string): Promise<ApiResponse<AuthStatusResponse>> {
    return this.makeRequest<AuthStatusResponse>(`/api/v1/auth/status?user_id=${userId}`);
  }

  // 3. Profile APIs
  async getProfile(userId: string): Promise<ApiResponse<ProfileResponse>> {
    return this.makeRequest<ProfileResponse>(`/api/v1/me?user_id=${userId}`);
  }

  async updateProfile(userId: string, profileData: any): Promise<ApiResponse<any>> {
    return this.makeRequest(`/api/v1/me?user_id=${userId}`, {
      method: 'PUT',
      body: JSON.stringify(profileData),
    });
  }

  // 4. Invite Request API
  async requestInvite(inviteData: {
    first_name: string;
    last_name: string;
    email: string;
    country: string;
    organisation: string;
    designation: string;
    linkedin_url?: string;
    referred_by_email?: string;
  }): Promise<ApiResponse<any>> {
    return this.makeRequest('/api/v1/invite/request', {
      method: 'POST',
      body: JSON.stringify(inviteData),
    });
  }

  // 4b. Signup API - Creates and immediately approves account
  async signup(signupData: {
    first_name: string;
    last_name: string;
    email: string;
    country: string;
    organisation: string;
    designation: string;
    linkedin_url?: string;
    city?: string;
    phone?: string;
    professional_summary?: string;
    rd_summary?: string;
  }): Promise<ApiResponse<any>> {
    return this.makeRequest('/api/v1/auth/signup', {
      method: 'POST',
      body: JSON.stringify({
        email: signupData.email,
        first_name: signupData.first_name,
        last_name: signupData.last_name,
        country: signupData.country,
        company: signupData.organisation,
        designation: signupData.designation,
        linkedin_url: signupData.linkedin_url,
        city: signupData.city,
        phone: signupData.phone,
        professional_summary: signupData.professional_summary,
        rd_summary: signupData.rd_summary,
      }),
    });
  }

  // 5. Concierge/Customer Support API
  async createConciergeCall(supportData: {
    name: string;
    email: string;
    areas: string[];
    message: string;
    slot_iso?: string;
    /** Human-readable form identifier so concierge ops know which surface the
     * lead came from (e.g. "about_us_contact_form", "support_modal"). */
    form_source?: string;
    /** Talk to Concierge: what was clicked and where (Backend/cta_alerts.py
     * keeps them on the row and puts them in the team alert). `surface` is
     * the key getTechnologyContacts takes; `page_url` the path only
     * (contactPagePath). */
    tech_id?: string;
    tech_title?: string;
    university?: string;
    surface?: string;
    page_url?: string;
  }, userId?: string): Promise<ApiResponse<any>> {
    const url = userId ? `/api/v1/concierge/calls?user_id=${userId}` : '/api/v1/concierge/calls';
    // The server caps these lengths and would refuse the whole lead (422)
    // over one long title, so trim them here rather than lose it.
    const clip = (v: string | undefined, n: number) => (typeof v === 'string' ? v.slice(0, n) : v);
    return this.makeRequest(url, {
      method: 'POST',
      body: JSON.stringify({
        ...supportData,
        tech_id: clip(supportData.tech_id, 64),
        tech_title: clip(supportData.tech_title, 500),
        university: clip(supportData.university, 300),
        surface: clip(supportData.surface, 40),
        page_url: clip(supportData.page_url, 500),
      }),
    });
  }

  // 6. Password Management API
  async setPassword(userId: string, password: string): Promise<ApiResponse<any>> {
    // Signed-in only: the server checks the bearer belongs to userId.
    return this.makeRequest(`/api/v1/password/set?user_id=${encodeURIComponent(userId)}`, {
      method: 'POST',
      headers: this.billingHeaders(),
      body: JSON.stringify({ password, confirm_password: password }),
    });
  }

  /**
   * Forgot-password: set a new password with the one-time token that
   * verifyOTP(…, 'password_reset') returned. Works signed out — the token,
   * not a session, says which account it is for.
   */
  async confirmPasswordReset(token: string, newPassword: string): Promise<ApiResponse<any>> {
    return this.makeRequest('/api/v1/auth/password/reset/confirm', {
      method: 'POST',
      body: JSON.stringify({ token, new_password: newPassword }),
    });
  }

  /**
   * Change an existing password, verifying the current one server-side.
   *
   * Distinct from `setPassword` above, which sets a password without proving
   * you know the old one. That is the right call for an account that has no
   * password yet (a Google/LinkedIn signup), and the wrong one for an account
   * that does — an unattended logged-in tab should not be enough to take the
   * account over. Use this whenever the user already has a password.
   *
   * Server errors arrive as codes (`current_password_incorrect`,
   * `no_password_set`, `user_not_found`); run them through
   * `friendlyAuthError` before showing them.
   */
  async changePassword(userId: string, currentPassword: string,
                       newPassword: string): Promise<ApiResponse<any>> {
    return this.makeRequest(`/api/v1/auth/password/change?user_id=${encodeURIComponent(userId)}`, {
      method: 'POST',
      headers: this.billingHeaders(),
      body: JSON.stringify({ current_password: currentPassword, new_password: newPassword }),
    });
  }

  // 6b. Account Deletion Request API
  /**
   * `reason` is the optional "why are you leaving" text from the modal. It is
   * kept with the request and copied onto the archive the admin side writes
   * before the account is actually removed, so send it only when the user
   * typed something — the endpoint still accepts an empty POST.
   */
  async requestAccountDeletion(userId: string, reason?: string): Promise<ApiResponse<any>> {
    const trimmed = (reason || '').trim();
    // The route now proves the caller owns this user_id (a request starts an
    // automatic deletion, so it can no longer be anonymous); billingHeaders
    // carries the bearer token and stays self-sufficient for the JSON body.
    return this.makeRequest(`/api/v1/users/${userId}/request-deletion`, {
      method: 'POST',
      headers: this.billingHeaders(),
      ...(trimmed ? { body: JSON.stringify({ reason: trimmed }) } : {}),
    });
  }

  // 7. AI Suggestions API
  async getAISuggestions(context: string = 'home', userId?: string): Promise<ApiResponse<{ suggestions: string[] }>> {
    const url = userId ? `/api/v1/ai/suggestions?context=${context}&user_id=${userId}` : `/api/v1/ai/suggestions?context=${context}`;
    return this.makeRequest(url, {
      method: 'GET',
    });
  }

  // 8. Referral Invites API
  async sendReferralInvite(userId: string, inviteData: {
    email: string;
    first_name: string;
    last_name: string;
    country: string;
    organisation: string;
    designation: string;
    linkedin_url?: string;
  }): Promise<ApiResponse<any>> {
    return this.makeRequest(`/api/v1/invites/referral?referring_user_id=${userId}`, {
      method: 'POST',
      body: JSON.stringify(inviteData),
    });
  }

  // 9. Feedback API
  async submitFeedback(userId: string, feedbackData: {
    category: string;
    message: string;
    rating?: number;
  }): Promise<ApiResponse<any>> {
    return this.makeRequest(`/api/v1/feedback?user_id=${userId}`, {
      method: 'POST',
      body: JSON.stringify({
        rating: feedbackData.rating || 5,
        feedback_text: feedbackData.message
      }),
    });
  }

  // 10. Bug Report API
  async reportBug(userId: string, bugCategory: string, bugDescription: string): Promise<ApiResponse<any>> {
    return this.makeRequest(`/api/v1/bugs/report?user_id=${userId}`, {
      method: 'POST',
      body: JSON.stringify({
        bug_category: bugCategory,
        bug_description: bugDescription
      }),
    });
  }

  // 11. Chat Management APIs
  async getUserChats(userId: string): Promise<ApiResponse<any[]>> {
    return this.makeRequest(`/api/v1/chats?user_id=${userId}`, {
      method: 'GET',
    });
  }

  async getChatMessages(chatSessionId: string): Promise<ApiResponse<any[]>> {
    // The session rides along: the server leaves the university out of the
    // stored cards for an Explorer, and needs to know who is reading.
    return this.makeRequest(`/api/v1/chats/${chatSessionId}/messages${this.sessionQuery()}`, {
      headers: this.billingHeaders(),
      method: 'GET',
    });
  }

  async submitChatFeedback(
    chatSessionId: string,
    messageId?: string,
    rating?: number,
    feedbackText?: string
  ): Promise<ApiResponse<any>> {
    const body: any = {};
    if (messageId) body.message_id = messageId;
    if (rating) body.rating = rating;
    if (feedbackText) body.feedback_text = feedbackText;

    return this.makeRequest(`/api/v1/chats/${chatSessionId}/feedback`, {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  // 12. Utility methods for session management
  // Using localStorage for persistent sessions across browser closes/refreshes
  // A View as member tab (services/viewAsState.ts) is signed in as the
  // member from its own sessionStorage, and never touches the admin's stored
  // sign-in: its getters read the view, its setters and clear do nothing to
  // localStorage.
  getStoredUserId(): string | null {
    if (isViewAsTab()) return liveViewAs()?.user_id || null;
    return localStorage.getItem('user_id');
  }

  setStoredUserId(userId: string): void {
    if (isViewAsTab()) return;
    localStorage.setItem('user_id', userId);
    // Every sign-in path (password, OTP, Google/LinkedIn, sign-up) comes
    // through here and a reload does not, so this is "just signed in" for
    // the upgrade prompt shown after sign-in (components/upgrade).
    try { localStorage.setItem('utech_signed_in_at', String(Date.now())); } catch { /* storage off */ }
    // Also update last activity timestamp
    this.updateLastActivity();
  }

  getStoredAccessToken(): string | null {
    if (isViewAsTab()) return liveViewAs()?.token || null;
    return localStorage.getItem('access_token');
  }

  setStoredAccessToken(token: string): void {
    if (isViewAsTab()) return;
    localStorage.setItem('access_token', token);
  }

  getStoredRefreshToken(): string | null {
    if (isViewAsTab()) return null;           // a view is not renewed: it ends
    return localStorage.getItem('refresh_token');
  }

  setStoredRefreshToken(token: string): void {
    if (isViewAsTab()) return;
    localStorage.setItem('refresh_token', token);
  }

  clearStoredAuth(): void {
    if (isViewAsTab()) { void this.endViewAs(); return; }      // signing out of a view ends it
    localStorage.removeItem('user_id');
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('last_activity');
  }
  
  // Activity tracking for inactivity timeout
  updateLastActivity(): void {
    localStorage.setItem('last_activity', Date.now().toString());
  }
  
  getLastActivity(): number {
    const lastActivity = localStorage.getItem('last_activity');
    return lastActivity ? parseInt(lastActivity, 10) : Date.now();
  }

  // Chat session management
  getStoredSessionId(): string | null {
    return sessionStorage.getItem('chat_session_id');
  }

  setStoredSessionId(sessionId: string): void {
    sessionStorage.setItem('chat_session_id', sessionId);
  }

  clearStoredSessionId(): void {
    sessionStorage.removeItem('chat_session_id');
  }

  // 13. Password Verification API
  async verifyPassword(userId: string, password: string): Promise<ApiResponse<any>> {
    return this.makeRequest(`/api/v1/password/verify?user_id=${encodeURIComponent(userId)}`, {
      method: 'POST',
      headers: this.billingHeaders(),
      body: JSON.stringify({ password }),
    });
  }

  // 14. Interests Management API
  async updateInterests(userId: string, interests: string[], mode: string = 'replace'): Promise<ApiResponse<any>> {
    return this.makeRequest(`/api/v1/interests?user_id=${userId}`, {
      method: 'PUT',
      body: JSON.stringify({ interests, mode }),
    });
  }

  // 15. Alerts Management API  
  async updateAlerts(userId: string, enabled: boolean): Promise<ApiResponse<any>> {
    return this.makeRequest(`/api/v1/alerts?user_id=${userId}`, {
      method: 'PUT',
      body: JSON.stringify({ enabled }),
    });
  }

  // 16. Saved Cards API
  async getSavedCards(userId: string, type: string = 'all'): Promise<ApiResponse<any>> {
    return this.makeRequest(`/api/v1/saved?user_id=${userId}&type=${type}`, {
      method: 'GET',
    });
  }

  async saveCard(payload: {
    asset_type: string;
    asset_id: string;
    bookmarked: boolean;
    liked: boolean | null;
    linked_chat_id: string | null;
  }, userId: string): Promise<ApiResponse<any>> {
    return this.makeRequest(`/api/v1/saved?user_id=${userId}`, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  }

  async likeCard(payload: {
    asset_type: string;
    asset_id: string;
    liked: boolean | null;
    linked_chat_id: string | null;
  }, userId: string): Promise<ApiResponse<any>> {
    return this.makeRequest(`/api/v1/liked?user_id=${userId}`, {
      method: 'POST',
      body: JSON.stringify({
        asset_type: payload.asset_type,
        asset_id: payload.asset_id,
        liked: payload.liked,
        linked_chat_id: payload.linked_chat_id
      }),
    });
  }

  async getLikedCards(userId: string, assetType: string = 'all'): Promise<ApiResponse<any>> {
    return this.makeRequest(`/api/v1/liked?user_id=${userId}&asset_type=${assetType}`, {
      method: 'GET',
    });
  }

  async getTechnologyDetails(techId: string): Promise<ApiResponse<any>> {
    // Extract reference number from full utech_technology_id
    // Format: UTECH-ARI-001553-1758806517159-57140968-3bdf-4554-b5d7-e26fa0018e3f
    // Extract: UTECH-ARI-001553
    const extractReferenceNumber = (fullId: string): string => {
      const parts = fullId.split('-');
      if (parts.length >= 3 && fullId.startsWith('UTECH-')) {
        return `${parts[0]}-${parts[1]}-${parts[2]}`;
      }
      return fullId; // Return as-is if doesn't match expected format
    };
    
    const referenceId = extractReferenceNumber(techId);
    console.log(`🔍 Tech ID extraction: ${techId} -> ${referenceId}`);
    
    // The session travels with the request so the backend can require one
    // (Admin → Tech URL pages → "Protect legacy tech detail endpoint"). Until
    // that switch is on the endpoint ignores both.
    return this.makeRequest(`/api/v1/technologies/${referenceId}${this.sessionQuery()}`, {
      method: 'GET',
      headers: this.billingHeaders(),
    });
  }

  async getResearcherDetails(researcherId: string): Promise<ApiResponse<any>> {
    return this.makeRequest(`/api/v1/researchers/${researcherId}${this.sessionQuery()}`, {
      method: 'GET',
      headers: this.billingHeaders(),
    });
  }

  /** "?user_id=<id>" for the signed-in user, "" otherwise. */
  private sessionQuery(): string {
    const uid = this.getStoredUserId();
    return uid ? `?user_id=${encodeURIComponent(uid)}` : '';
  }

  // ---- University contacts (metered) ---------------------------------------
  // Contact addresses no longer ride on GET /technologies/{id}: they come from
  // /technologies/{id}/contacts, which counts each distinct technology against
  // the account's limit (Backend/contact_limits.py). Signed-in only, so these
  // answer a 401-shaped response without calling out when there is no session.
  private signedOut<T>(): ApiResponse<T> {
    return { success: false, error: 'Please sign in to see university contacts.', status: 401 };
  }

  /**
   * `surface` says where the Contact University sheet was opened —
   * search_results (Direct Search list card), tech_modal (the technology
   * window, incl. from Saved), saved (Saved page's own tech card),
   * saved_researcher, chat, tech_page (public /tech page) or send_email —
   * and goes with the page path. `purpose: 'send'` marks the fetch made to
   * send an in-app e-mail rather than to show the sheet. The server records
   * every opening and may e-mail the team (Backend/cta_alerts.py), so the
   * sheets no longer post a lead of their own for it.
   */
  async getTechnologyContacts(
    techId: string, opts?: { surface?: string; purpose?: 'open' | 'send' },
  ): Promise<ApiResponse<TechContactsResponse>> {
    const uid = this.getStoredUserId();
    if (!uid) return this.signedOut();
    // Same id rule as getTechnologyDetails: both endpoints match the
    // reference id (UTECH-ARI-001553), not the long legacy form.
    const parts = techId.split('-');
    const referenceId = parts.length >= 3 && techId.startsWith('UTECH-')
      ? `${parts[0]}-${parts[1]}-${parts[2]}` : techId;
    const params = new URLSearchParams({ user_id: uid });
    if (opts?.surface) params.set('surface', opts.surface.slice(0, 40));
    if (opts) {
      const page = contactPagePath();
      if (page) params.set('page', page);
    }
    if (opts?.purpose) params.set('purpose', opts.purpose);
    const res = await this.makeRequest<TechContactsResponse>(
      `/api/v1/technologies/${encodeURIComponent(referenceId)}/contacts?${params.toString()}`,
      { method: 'GET', headers: this.billingHeaders() });

    // TODO(contact-limits): remove fallback once backend 2026-10 is live
    // FastAPI's own "Not Found" means the route is missing (older backend);
    // an unknown technology answers "not_found". Until the new backend is out
    // the details endpoint still carries the four fields, unmetered.
    if (!res.success && res.status === 404 && res.error === 'Not Found') {
      const old = await this.getTechnologyDetails(techId);
      if (!old.success || !old.data) return { success: false, error: old.error, status: old.status };
      const d = old.data;
      return {
        success: true,
        status: old.status,
        data: {
          allowed: true,
          contacts: {
            tto_office_details: d.tto_office_details ?? null,
            tech_manager_default: d.tech_manager_default ?? {},
            common_email: d.common_email ?? null,
            tech_manager_details: d.tech_manager_details ?? [],
          },
          usage: null,
        },
      };
    }
    return res;
  }

  async getContactLimitStatus(): Promise<ApiResponse<ContactUsage>> {
    const uid = this.getStoredUserId();
    if (!uid) return this.signedOut();
    return this.makeRequest<ContactUsage>(
      `/api/v1/contact-limit/status?user_id=${encodeURIComponent(uid)}`,
      { method: 'GET', headers: this.billingHeaders() });
  }

  /** "Request more" — stored for Admin → Users → Contact limits and e-mailed
   *  to the team. `duplicate: true` means one was already open; nothing new
   *  was sent. */
  async requestMoreContacts(payload: {
    reason: string; extra_requested?: number; name?: string;
  }): Promise<ApiResponse<ContactRequestResponse>> {
    const uid = this.getStoredUserId();
    if (!uid) return this.signedOut();
    return this.makeRequest<ContactRequestResponse>(
      `/api/v1/contact-limit/request?user_id=${encodeURIComponent(uid)}`, {
        method: 'POST', headers: this.billingHeaders(), body: JSON.stringify(payload),
      });
  }

  async aiCategorizeTechnologies(payload: {
    technologies: any[];
    user_keywords: string[];
  }): Promise<ApiResponse<any>> {
    return this.makeRequest('/api/v1/ai/categorize-technologies', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  }

  async preloadRecommendations(userId: string): Promise<void> {
    /**
     * Preload recommendations in background without blocking UI.
     * This is called during login to cache recommendations for faster For You page loading.
     * Now using V2 with GPT-enhanced parallel keyword searches.
     */
    try {
      console.log(`🎯 Preloading V2 recommendations for user ${userId}`);
      const response = await this.getPersonalizedRecommendationsV2(userId);
      
      if (response.success) {
        console.log(`✅ Preloaded ${response.data?.total_results || 0} recommendations across ${response.data?.total_categories || 0} categories`);
        console.log(`⏱️ Total time: ${response.data?.processing_time}s (Enhancement: ${response.data?.enhancement_time}s, Search: ${response.data?.search_time}s, Scoring: ${response.data?.scoring_time}s)`);
      } else {
        console.log(`ℹ️ Recommendation preload: ${response.error || 'No action needed'}`);
      }
    } catch (error) {
      console.warn('Background recommendation preloading failed:', error);
    }
  }

  async getAllTechnologyKeywords(): Promise<ApiResponse<any>> {
    return this.makeRequest('/api/v1/technologies/keywords', {
      method: 'GET',
    });
  }

  async checkQueryScope(
    query: string,
    context: string = "technology_transfer"
  ): Promise<ApiResponse<any>> {
    const body = {
      query,
      context
    };

    return this.makeRequest<any>('/api/v1/llm/scope-check', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  // Email tracking APIs
  async sendTrackedEmail(payload: {
    to_email: string;
    subject: string;
    content: string;
    conversation_id: string;
    user_id: string;
    tracking_consent: boolean;
    is_reply: boolean;
    cc_email?: string; // Optional CC address(es) (e.g. common_email; may be a ';'/','-separated list)
    utech_technology_id?: string; // Persisted on email_conversations for tech lookup
  }): Promise<ApiResponse<any>> {
    return this.makeRequest('/api/v1/email/send-tracked', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  }

  async getEmailConversations(userId: string): Promise<ApiResponse<any>> {
    return this.makeRequest(`/api/v1/email/conversations?user_id=${userId}`, {
      method: 'GET',
    });
  }

  async getEmailConversation(conversationId: string): Promise<ApiResponse<any>> {
    return this.makeRequest(`/api/v1/email/conversations/${conversationId}`, {
      method: 'GET',
    });
  }

  // 17. Logout API
  async logout(userId: string, everywhere: boolean = false): Promise<ApiResponse<any>> {
    if (isViewAsTab()) return this.endViewAs();
    // Both tokens: the server ends a session only for a caller holding it, and
    // the refresh token names it even when this tab's access token is one
    // renewal behind another tab's.
    return this.makeRequest(`/api/v1/auth/logout?user_id=${userId}&everywhere=${everywhere}`, {
      method: 'POST',
      body: JSON.stringify({ access_token: this.getStoredAccessToken(), refresh_token: this.getStoredRefreshToken() }),
    });
  }

  /** End this tab's View as member (Backend/admin_view_as.py). */
  async endViewAs(): Promise<ApiResponse<any>> {
    const v = viewAs();
    markViewAsEnded();
    if (!v?.token) return { success: true };
    try {
      await fetch(`${this.baseUrl}/api/v1/view-as/end`, {
        method: 'POST', mode: 'cors', credentials: 'omit', headers: { [VIEW_AS_HEADER]: v.token },
      });
    } catch { /* it expires on its own */ }
    return { success: true };
  }

  // Text enhancement endpoint
  async enhanceText(text: string, textType: 'professional_summary' | 'rd_summary', userId: string, linkedinUrl?: string, companyLinkedinUrl?: string): Promise<ApiResponse<{enhanced_text: string, context_used: boolean}>> {
    const params = new URLSearchParams({
      text,
      text_type: textType,
      user_id: userId
    });
    
    if (linkedinUrl) params.append('linkedin_url', linkedinUrl);
    if (companyLinkedinUrl) params.append('company_linkedin_url', companyLinkedinUrl);
    
    return this.makeRequest<{enhanced_text: string, context_used: boolean}>(`/api/v1/text/enhance?${params.toString()}`, {
      method: 'POST'
    });
  }

  // File management endpoints
  async uploadFile(userId: string, file: File, filePurpose: 'rd_profile_deck' | 'chat_attachment', chatSessionId?: string): Promise<ApiResponse<{file_id: string, file_name: string, processing_info: any}>> {
    try {
      // Convert file to base64
      const base64Content = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
          const result = reader.result as string;
          const base64 = result.split(',')[1];
          resolve(base64);
        };
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });

      const body = {
        user_id: userId,
        file_data: {
          name: file.name,
          type: file.type,
          size: file.size,
          content: base64Content
        },
        file_purpose: filePurpose,
        chat_session_id: chatSessionId || null
      };

      return this.makeRequest('/api/v1/files/upload', {
        method: 'POST',
        body: JSON.stringify(body)
      });
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'File upload failed'
      };
    }
  }

  async getUserFiles(userId: string, filePurpose?: string): Promise<ApiResponse<{files: any[], count: number}>> {
    const params = filePurpose ? `?file_purpose=${filePurpose}` : '';
    return this.makeRequest(`/api/v1/files/user/${userId}${params}`, {
      method: 'GET'
    });
  }

  async deleteUserFile(fileId: string, userId: string): Promise<ApiResponse<{message: string}>> {
    return this.makeRequest(`/api/v1/files/${fileId}?user_id=${userId}`, {
      method: 'DELETE'
    });
  }

  async getChatAttachments(chatSessionId: string): Promise<ApiResponse<{files: any[], count: number}>> {
    return this.makeRequest(`/api/v1/files/chat/${chatSessionId}`, {
      method: 'GET'
    });
  }

  async enhanceTextWithFile(text: string, textType: 'professional_summary' | 'rd_summary', userId: string, file?: File, linkedinUrl?: string, companyLinkedinUrl?: string): Promise<ApiResponse<{enhanced_text: string, context_used: boolean, file_processed: boolean, images_processed: number}>> {
    let fileData = null;
    
    if (file) {
      try {
        // Convert file to base64
        const base64Content = await new Promise<string>((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => {
            const result = reader.result as string;
            const base64 = result.split(',')[1];
            resolve(base64);
          };
          reader.onerror = reject;
          reader.readAsDataURL(file);
        });

        fileData = {
          name: file.name,
          type: file.type,
          size: file.size,
          content: base64Content
        };
      } catch (error) {
        console.error('Error processing file:', error);
      }
    }

    const body = {
      text,
      text_type: textType,
      user_id: userId,
      linkedin_url: linkedinUrl || null,
      company_linkedin_url: companyLinkedinUrl || null,
      file_data: fileData
    };
    
    return this.makeRequest<{enhanced_text: string, context_used: boolean, file_processed: boolean, images_processed: number}>('/api/v1/text/enhance-with-file', {
      method: 'POST',
      body: JSON.stringify(body)
    });
  }

  async generateFollowUpQuestions(
    chatHistory: any[], 
    currentContext?: string
  ): Promise<ApiResponse<{questions: string[], status: string}>> {
    return this.makeRequest('/api/v1/llm/followup-questions', {
      method: 'POST',
      body: JSON.stringify({
        chat_history: chatHistory,
        current_context: currentContext
      })
    });
  }

  async getAllTechnologyKeywordsV2(): Promise<ApiResponse<{
    success: boolean,
    keywords_base64: string,
    keywords_preview: string[],
    total_count: number,
    source_technologies: number,
    encoding: string,
    decode_instructions: string
  }>> {
    return this.makeRequest('/api/v2/technologies/keywords', {
      method: 'GET'
    });
  }

  async autoRecommendInterests(userId: string): Promise<ApiResponse<{
    success: boolean,
    message: string,
    keywords_assigned: string[],
    total_interests?: number,
    all_interests?: string[],
    reason?: string,
    error?: string
  }>> {
    return this.makeRequest(`/api/v1/me/interests/auto-recommend?user_id=${userId}`, {
      method: 'POST'
    });
  }

  async getPersonalizedRecommendations(userId: string): Promise<ApiResponse<{
    success: boolean,
    query_used: string,
    total_results: number,
    results: any[],
    categorized_results: Record<string, any[]>,
    summary: string,
    processing_time: number,
    query_generation: {
      reasoning: string,
      focus_areas: string[],
      user_interests: string[]
    },
    error?: string
  }>> {
    return this.makeRequest(`/api/v1/me/recommendations?user_id=${userId}`, {
      method: 'GET',
      headers: this.billingHeaders(),
    });
  }

  // 7c. V2 Recommendations API - Parallel keyword searches with AI scoring and GPT enhancement
  async getPersonalizedRecommendationsV2(userId: string, forceRefresh: boolean = false): Promise<ApiResponse<{
    success: boolean,
    version: string,
    categories: Record<string, {
      results: any[],
      count: number,
      enhanced_query: string
    }>,
    total_results: number,
    total_categories: number,
    user_interests: string[],
    processing_time: number,
    enhancement_time: number,
    search_time: number,
    scoring_time: number,
    error?: string,
    message?: string
  }>> {
    const refreshParam = forceRefresh ? '&force_refresh=true' : '';
    return this.makeRequest(`/api/v1/me/recommendations/v2?user_id=${userId}${refreshParam}`, {
      method: 'GET',
      headers: this.billingHeaders(),
    });
  }

  // ============================================================
  // Direct Search — keyword + filter search over the catalog.
  // Two backends behind the same shape: 'algolia' and 'native'.
  // ============================================================
  // Both carry the session: an Explorer gets no University facet and no
  // university on the cards, decided by the server for whoever is signed in.
  async getDirectSearchFacets(): Promise<ApiResponse<{ facets: DirectSearchFacets }>> {
    return this.makeRequest(`/api/v1/direct-search/facets${this.sessionQuery()}`,
      { method: 'GET', headers: this.billingHeaders() });
  }

  async getDirectSearchPopular(): Promise<ApiResponse<DirectSearchPopularResponse>> {
    return this.makeRequest<DirectSearchPopularResponse>(`/api/v1/direct-search/popular${this.sessionQuery()}`,
      { method: 'GET', headers: this.billingHeaders() });
  }

  async directSearch(
    backend: DirectSearchBackend,
    body: DirectSearchRequest,
  ): Promise<ApiResponse<DirectSearchResponse>> {
    // 'hybrid' is the product's default backend literal. Where it goes is
    // decided by SEARCH_ENDPOINT (today: the search_v2 orchestrator, whose
    // engine is picked in the admin panel; previously the Typesense+Pinecone
    // hybrid path). The literal itself is kept as-is because history rows,
    // analytics props and the page state all carry it. Algolia and native
    // remain available and unchanged.
    const path =
      backend === 'hybrid' ? SEARCH_ENDPOINT
      : backend === 'algolia' ? '/api/v1/direct-search/algolia'
      : '/api/v1/direct-search/native';
    // Attach the signed-in user so `search_query_logs` rows are attributable.
    // Done here rather than at each call site: the logs are only useful for
    // debugging "my search returned nothing" if every search carries the id.
    // Anonymous users stay anonymous (field omitted, column stays NULL).
    const userId = body.user_id ?? this.getStoredUserId() ?? undefined;
    const init: RequestInit = {
      method: 'POST',
      // The bearer token lets the server VERIFY who is searching rather than
      // trusting the id in the body — it decides the free-tier caps.
      headers: this.billingHeaders(),
      body: JSON.stringify(userId ? { ...body, user_id: userId } : body),
    };
    let res = await this.makeRequest<DirectSearchResponse>(path, init);
    // Safety net for the v2 rollout only: a 404 means the route is not
    // registered on the server we are talking to (older deploy, path
    // mismatch) — not a search failure — so replay on the legacy path rather
    // than showing users an empty catalog. 5xx / {success:false} are NOT
    // retried: those are real answers from a live v2.
    if (path === SEARCH_ENDPOINT && SEARCH_ENDPOINT !== LEGACY_SEARCH_ENDPOINT
        && !res.success && res.status === 404) {
      console.warn(`[search] ${SEARCH_ENDPOINT} not found on this server — replaying on ${LEGACY_SEARCH_ENDPOINT}`);
      res = await this.makeRequest<DirectSearchResponse>(LEGACY_SEARCH_ENDPOINT, init);
    }
    // v2 explains itself in `meta` (route, engine, timings). End users never
    // see any of it; in dev builds it goes to the console for tuning.
    const meta = res.data?.meta;
    if (meta && (import.meta as any).env?.DEV) {
      console.debug('[search v2]', meta.route, meta.engine,
        meta.classification?.source, meta.served_from_cache ? 'cached' : 'fresh',
        body.force_route ? 'forced' : '', meta.timings, meta);
    }
    return res;
  }

  /**
   * The public homepage's teaser search (3 cards). Its own endpoint rather
   * than direct-search/algolia: it runs on Typesense (Algolia only as the
   * server's fallback) and the server logs every call to search_query_logs
   * with the visit it came from, so Search Intel → Homepage searches can show
   * what each anonymous visitor searched and what they did next.
   *
   * `tracking` should be visitorTracking's in-memory ids (a second tab can
   * start a new session in localStorage); localStorage is the fallback.
   * The bearer token rides along when present — the server attaches the user
   * only if it verifies, otherwise the search stays anonymous.
   *
   * The server answers {success, data: {...}}; this unwraps it so callers get
   * the payload as `res.data`, like every other method here.
   */
  async homeSearch(
    query: string,
    source: HomeSearchSource,
    tracking: { visitor_id?: string; session_id?: string } = {},
  ): Promise<ApiResponse<HomeSearchData>> {
    const read = (k: string): string => { try { return localStorage.getItem(k) || ''; } catch { return ''; } };
    const visitorId = tracking.visitor_id || read('utech_visitor_id');
    const sessionId = tracking.session_id || read('utech_session_id');
    const userId = this.getStoredUserId() || '';
    const body: Record<string, string> = { query, source };
    if (visitorId) body.visitor_id = visitorId;
    if (sessionId) body.session_id = sessionId;
    if (userId) body.user_id = userId;
    const res = await this.makeRequest<any>('/api/v1/home/search', {
      method: 'POST',
      headers: this.billingHeaders(),
      body: JSON.stringify(body),
    });
    const env = res.data;
    if (res.success && env?.success && env.data) {
      return { success: true, data: env.data as HomeSearchData, status: res.status };
    }
    return {
      success: false,
      error: res.status === 429 ? 'rate_limited' : (env?.error || res.error || 'Search failed'),
      status: res.status,
    };
  }

  // ---- Billing (Razorpay) -------------------------------------------------
  // Unlike the rest of the API, billing endpoints REQUIRE the bearer token:
  // the backend validates it against user_sessions before touching money.
  private billingHeaders(): Record<string, string> {
    // makeRequest spreads `...options` AFTER its default headers, so a custom
    // headers object REPLACES the defaults rather than merging. These must
    // therefore be self-sufficient — dropping Content-Type here turns every
    // POST body into an unparseable blob server-side (422 "Field required").
    const token = this.getStoredAccessToken();
    return {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    };
  }

  // ---- Profile spec (fields, options, weights) ----------------------------
  // The form and the completion score are both driven by this, so the browser
  // and the server can no longer disagree about how complete a profile is.
  async getEntitlements(userId: string): Promise<ApiResponse<any>> {
    return this.makeRequest(`/api/v1/entitlements?user_id=${encodeURIComponent(userId)}`,
      { headers: this.billingHeaders() });
  }

  async getOnboardingStatus(userId: string): Promise<ApiResponse<any>> {
    return this.makeRequest(`/api/v1/onboarding/status?user_id=${encodeURIComponent(userId)}`,
      { headers: this.billingHeaders() });
  }

  async getOnboardingKeywords(industry: string): Promise<ApiResponse<any>> {
    return this.makeRequest(
      `/api/v1/onboarding/keywords?industry=${encodeURIComponent(industry)}`);
  }

  /** Old bodies ({industry}, {industry, keyword}, {completed}, {skipped}) are
   *  still valid; v2 adds `event` and the timing/attribution fields, which an
   *  older server ignores. */
  async saveOnboarding(userId: string, payload: OnboardingSavePayload): Promise<ApiResponse<any>> {
    return this.makeRequest('/api/v1/onboarding/save', {
      method: 'POST', headers: this.billingHeaders(),
      body: JSON.stringify({ user_id: userId, ...payload }),
    });
  }

  /** Public onboarding config (flow switch, industries with their chips, the
   *  modal + landing copy). A 404 (older server) means "flow v1". */
  async getOnboardingConfig(): Promise<ApiResponse<OnboardingPublicConfig & { success: boolean }>> {
    return this.makeRequest('/api/v1/onboarding/config');
  }

  /** The Explorer's 3 precomputed Cosmos picks. Bearer-checked like status.
   *  With no industry/keyword/outcome the server reads the saved choice. */
  async getOnboardingPicks(userId: string, q: PicksQuery = {}): Promise<ApiResponse<PicksResponse>> {
    const p = new URLSearchParams({ user_id: userId });
    if (q.industry) p.set('industry', q.industry);
    if (q.keyword) p.set('keyword', q.keyword);
    if (q.outcome) p.set('outcome', q.outcome);
    if (q.ob_id) p.set('ob_id', q.ob_id);
    if (q.first !== undefined) p.set('first', q.first ? '1' : '0');
    if (q.seed) p.set('seed', '1');
    return this.makeRequest(`/api/v1/onboarding/picks?${p.toString()}`,
      { headers: this.billingHeaders() });
  }

  /** What's New page (Backend/whatsnew_routes.py): platform updates + the
   *  curated news, filtered. Bearer-checked; the server caches 5 minutes. */
  async getWhatsNew(userId: string, q: { category?: string; industry?: string; from?: string; to?: string } = {}):
    Promise<ApiResponse<any>> {
    const p = new URLSearchParams({ user_id: userId });
    if (q.category && q.category !== 'all') p.set('category', q.category);
    if (q.industry) p.set('industry', q.industry);
    if (q.from) p.set('from', q.from);
    if (q.to) p.set('to', q.to);
    return this.makeRequest(`/api/v1/whatsnew?${p.toString()}`, { headers: this.billingHeaders() });
  }

  /** One "Technology changes" card's tech offers (Backend whatsnew.change_items). */
  async getWhatsNewChange(userId: string, id: string): Promise<ApiResponse<any>> {
    const p = new URLSearchParams({ user_id: userId, id });
    return this.makeRequest(`/api/v1/whatsnew/change?${p.toString()}`, { headers: this.billingHeaders() });
  }

  /** Which gated pages this account may open (Backend/feature_access.py):
   *  {success, features: {foryou|whatsnew|ocean: {enabled, mode, preview}}}.
   *  Bearer-checked. utils/featureAccess.ts treats anything but a clean
   *  answer as "not enabled". */
  async getFeatureAccess(userId: string): Promise<ApiResponse<any>> {
    return this.makeRequest(`/api/v1/feature-access?user_id=${encodeURIComponent(userId)}`,
      { headers: this.billingHeaders() });
  }

  /** Non-state onboarding events (shown, chips_empty, error) for the
   *  server-side history. Fire-and-forget; a 404 from an older server is fine. */
  async logOnboardingEvent(userId: string, body: OnboardingEventBody): Promise<ApiResponse<any>> {
    return this.makeRequest('/api/v1/onboarding/event', {
      method: 'POST', headers: this.billingHeaders(),
      body: JSON.stringify({ user_id: userId, ...body }),
    });
  }

  async adminOnboardingSettings(userId: string): Promise<ApiResponse<any>> {
    return this.makeRequest(`/api/v1/admin/onboarding/settings?user_id=${encodeURIComponent(userId)}`,
      { headers: this.billingHeaders() });
  }

  async adminSetFreeTierLock(userId: string, payload: Record<string, any>): Promise<ApiResponse<any>> {
    return this.makeRequest(`/api/v1/admin/onboarding/lock?user_id=${encodeURIComponent(userId)}`, {
      method: 'POST', headers: this.billingHeaders(), body: JSON.stringify(payload),
    });
  }

  async getProfileSpec(): Promise<ApiResponse<ProfileSpec>> {
    return this.makeRequest<ProfileSpec>('/api/v1/profile/spec');
  }

  async getBillingSummary(userId: string): Promise<ApiResponse<BillingSummary>> {
    const res = await this.makeRequest<BillingSummary>(`/api/v1/billing/summary?user_id=${encodeURIComponent(userId)}`, {
      headers: this.billingHeaders(),
    });
    if (res.success && res.data?.success) {
      try {
        window.dispatchEvent(new CustomEvent(BILLING_SUMMARY_EVENT, { detail: { userId, data: res.data } }));
      } catch { /* no window */ }
    }
    return res;
  }

  /** May this account copy tech-card text while the copy guard is on
   *  (utils/copyGuard.ts; admin → Tech URL pages → Who can still copy)?
   *  Bearer-checked like billing, so a 401 renews the token and asks again. */
  async getCopyPermission(userId: string): Promise<ApiResponse<{ success?: boolean; allowed?: boolean; reason?: string }>> {
    return this.makeRequest(`/api/v1/ui/copy-permission?user_id=${encodeURIComponent(userId)}`, {
      headers: this.billingHeaders(),
    });
  }

  /** `coupon` is honoured only when a promotion carries a Razorpay offer id —
   *  a recurring charge takes its price from the gateway-side plan, so that
   *  offer is the only thing able to discount it. */
  /** `payMethod` is the payment method the customer chose ('card' | 'upi').
   *  A Razorpay subscription offer covers one method and only one offer can
   *  ride on a subscription, so the server needs to know which before it
   *  creates it. */
  /** `trial` defers the first charge: the customer sets up autopay now and is
   *  charged when the trial ends. Only valid where the server says
   *  trial.mode === 'mandate'. */
  /** `expectedAmount` is the first-cycle price the button showed (minor
   *  units). The server refuses with 409 if it would now charge more —
   *  a campaign that ended between the quote and the click must not turn
   *  into a silent full-price sign-up. */
  async billingSubscribe(userId: string, planCode: string, coupon?: string,
                         payMethod?: string, trial = false,
                         expectedAmount?: number, renewal = false): Promise<ApiResponse<any>> {
    return this.makeRequest('/api/v1/billing/subscribe', {
      method: 'POST', headers: this.billingHeaders(),
      body: JSON.stringify({ user_id: userId, plan_code: planCode,
                             coupon: coupon || null, method: payMethod || null,
                             trial, renewal,
                             expected_amount: Number.isFinite(expectedAmount) ? expectedAmount : null }),
    });
  }

  /** Price a plan with a coupon WITHOUT creating an order or redeeming
   *  anything. Runs the same server-side pricing checkout runs, so the amount
   *  shown is the amount charged. */
  async billingCouponPreview(userId: string, planCode: string, coupon?: string,
                             autoRenew: boolean = true): Promise<ApiResponse<any>> {
    return this.makeRequest('/api/v1/billing/coupon/preview', {
      method: 'POST', headers: this.billingHeaders(),
      // The two flows price differently; quoting the wrong one would show a
      // figure checkout will not charge.
      body: JSON.stringify({ user_id: userId, plan_code: planCode,
                             coupon: coupon || null, auto_renew: autoRenew }),
    });
  }

  async billingOrder(userId: string, planCode: string, coupon?: string): Promise<ApiResponse<any>> {
    return this.makeRequest('/api/v1/billing/order', {
      method: 'POST', headers: this.billingHeaders(),
      body: JSON.stringify({ user_id: userId, plan_code: planCode, coupon: coupon || null }),
    });
  }

  async billingVerify(payload: {
    user_id: string; flow: 'order' | 'subscription'; plan_code: string;
    razorpay_payment_id: string; razorpay_signature: string;
    razorpay_order_id?: string; razorpay_subscription_id?: string;
  }): Promise<ApiResponse<any>> {
    return this.makeRequest('/api/v1/billing/verify', {
      method: 'POST', headers: this.billingHeaders(),
      body: JSON.stringify(payload),
    });
  }

  /** Cancel the renewal of a paid membership (access runs to the period's
   *  end). Takes the same reasons as the trial exit: the route rejects an
   *  empty list, so the tab only calls this from the sheet. The defaults
   *  only keep the old one-argument call compiling for any stale caller —
   *  the server still answers it with "Pick at least one reason." (The admin
   *  panel does not come through here; it has adminBillingCancelSub.)
   *
   *  Answers {cancelled, access_until, gateway, winback}. `winback` is the
   *  come-back coupon ({code, promo_id, discount_label, message, plan}) when
   *  the admin's win-back offer is on for memberships, else null; the same
   *  code goes out in the cancellation e-mail. The admin reads and sets that
   *  offer through the panel dispatcher: GET users/winback_settings and POST
   *  users/winback_settings_set (adminPanelGet / adminPanelPost). */
  async billingCancel(userId: string, reasons: string[] = [], other = ''): Promise<ApiResponse<any>> {
    return this.makeRequest('/api/v1/billing/cancel', {
      method: 'POST', headers: this.billingHeaders(),
      body: JSON.stringify({ user_id: userId, reasons, other }),
    });
  }

  /** Separate from billingCancel: a trial ending is the one exit we get to
   *  ask "why" at, and the reasons are filed alongside the cancellation.
   *  Same answer shape as billingCancel, `winback` included. */
  async billingCancelTrial(userId: string, reasons: string[], other: string): Promise<ApiResponse<any>> {
    return this.makeRequest('/api/v1/billing/trial/cancel', {
      method: 'POST', headers: this.billingHeaders(),
      body: JSON.stringify({ user_id: userId, reasons, other }),
    });
  }

  /** One row per step of the cancel-sheet flow — 'opened' the sheet, 'kept'
   *  the trial or membership — so the admin panel can see who even clicked,
   *  not just who left. 'confirmed' is filed by the server inside the cancel
   *  itself; the tab never sends it. `kind` says which sheet it was: the same
   *  route and table serve both, keyed by a kind column, so the name stays
   *  "trial" even though memberships log through it too. Callers
   *  fire-and-forget: a lost click must not hold up the sheet, and
   *  makeRequest never rejects. */
  async billingTrialEvent(userId: string, event: string, reasons: string[] = [],
                          other = '', kind: 'trial' | 'subscription' = 'trial'): Promise<ApiResponse<any>> {
    return this.makeRequest('/api/v1/billing/trial/event', {
      method: 'POST', headers: this.billingHeaders(),
      body: JSON.stringify({ user_id: userId, event, reasons, other, kind }),
    });
  }

  // ---- Billing admin (/admin page) ----
  async adminViews(userId: string): Promise<ApiResponse<any>> {
    return this.makeRequest(`/api/v1/admin/views?user_id=${encodeURIComponent(userId)}`,
      { headers: this.billingHeaders() });
  }

  /**
   * Generic read from the admin-panel dispatcher (admin_service.panel_get).
   *
   * The caller travels as `caller_id`, never `user_id`: handlers take their own
   * `user_id` meaning "the user being inspected", and sending both under one
   * name made the server authenticate as the inspected user.
   *
   * Empty strings are sent, not dropped. For several handlers "" is a real
   * value that means "no exclusions" / "no filter", and silently omitting the
   * key made the server fall back to its defaults instead.
   */
  async adminPanelGet(userId: string, section: string, action: string,
                      params: Record<string, string | number> = {}): Promise<ApiResponse<any>> {
    const qs = Object.entries(params)
      .filter(([, v]) => v !== undefined && v !== null)
      .map(([k, v]) => `&${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`)
      .join('');
    return this.makeRequest(
      `/api/v1/admin/panel/${section}/${action}?caller_id=${encodeURIComponent(userId)}${qs}`,
      { headers: this.billingHeaders() });
  }

  /** Generic mutation through the admin-panel dispatcher (audit-logged server-side). */
  async adminPanelPost(userId: string, section: string, action: string,
                       body: Record<string, any> = {}): Promise<ApiResponse<any>> {
    return this.makeRequest(
      `/api/v1/admin/panel/${section}/${action}?caller_id=${encodeURIComponent(userId)}`,
      { method: 'POST', headers: this.billingHeaders(), body: JSON.stringify(body) });
  }

  /**
   * Fetch a FILE from one of the raw admin routes — the ones that stream a
   * body (CSV, XLSX, a workbook, a doc) instead of the panel dispatcher's JSON
   * envelope, which cannot carry a 100 MB export. Same bearer header as every
   * admin call; the caller travels as `user_id` because these routes are
   * declared like the billing admin routes, not like the panel dispatcher.
   *
   * Returns the blob plus the filename the server chose (Content-Disposition),
   * or an error string normalised the same way makeRequest does. One token
   * renewal on 401, for the same reason makeRequest has one: the access token
   * dies on an absolute clock while the admin is mid-session.
   */
  async adminFetchFile(userId: string, path: string,
                       params: Record<string, string | number> = {}, isRetry = false):
    Promise<{ blob: Blob | null; filename: string; error: string }> {
    const qs = Object.entries(params)
      .filter(([, v]) => v !== undefined && v !== null && v !== '')
      .map(([k, v]) => `&${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`)
      .join('');
    const url = `${this.baseUrl}${path}?user_id=${encodeURIComponent(userId)}${qs}`;
    try {
      this.updateLastActivity();
      const sent = this.getStoredAccessToken();
      const res = await fetch(url, {
        headers: this.billingHeaders(), mode: 'cors', credentials: 'omit',
      });
      if (!res.ok) {
        if (res.status === 401 && !isRetry && this.getStoredRefreshToken()) {
          // As in makeRequest: a session renewed while this was in flight is
          // reused, not renewed again (that would void the new token).
          const stored = this.getStoredAccessToken();
          const fresh = sent && stored && stored !== sent ? stored : await this.refreshAccessToken();
          if (fresh) return this.adminFetchFile(userId, path, params, true);
        }
        if (res.status === 401 && sent && (!this.getStoredRefreshToken() || ApiService.refreshRefused)) {
          this.signalSessionExpired();
        }
        const data = await res.json().catch(() => ({} as any));
        return { blob: null, filename: '', error: this.normalizeApiError(data?.detail, res.status) };
      }
      // filename="x.csv" or filename*=UTF-8''x.csv — take whichever is there.
      const cd = res.headers.get('content-disposition') || '';
      const star = /filename\*=(?:UTF-8'')?([^;]+)/i.exec(cd);
      const plain = /filename="?([^";]+)"?/i.exec(cd);
      let filename = '';
      try { filename = star ? decodeURIComponent(star[1].trim()) : (plain ? plain[1].trim() : ''); }
      catch { filename = plain ? plain[1].trim() : ''; }
      return { blob: await res.blob(), filename, error: '' };
    } catch (e: any) {
      return { blob: null, filename: '', error: e?.message || 'Network error' };
    }
  }

  async adminSetViews(userId: string, hiddenGroups: string[]): Promise<ApiResponse<any>> {
    return this.makeRequest(`/api/v1/admin/views?user_id=${encodeURIComponent(userId)}`, {
      method: 'POST', headers: this.billingHeaders(),
      body: JSON.stringify({ hidden_groups: hiddenGroups }),
    });
  }

  async adminBillingCheck(userId: string): Promise<ApiResponse<any>> {
    return this.makeRequest(`/api/v1/admin/billing/check?user_id=${encodeURIComponent(userId)}`, {
      headers: this.billingHeaders(),
    });
  }

  /** Delete one account's billing history, returning it to the free tier. */
  async adminClearUserBilling(userId: string, targetUserId: string,
                              cancelAtGateway = true): Promise<ApiResponse<any>> {
    return this.makeRequest(`/api/v1/admin/billing/clear-user?user_id=${encodeURIComponent(userId)}`, {
      method: 'POST',
      headers: this.billingHeaders(),
      body: JSON.stringify({ target_user_id: targetUserId, confirm: true,
                             cancel_at_gateway: cancelAtGateway }),
    });
  }

  /** Wipe EVERY account's billing history. `confirmPhrase` must be exact. */
  async adminResetAllBilling(userId: string, confirmPhrase: string,
                             cancelAtGateway = true): Promise<ApiResponse<any>> {
    return this.makeRequest(`/api/v1/admin/billing/reset-all?user_id=${encodeURIComponent(userId)}`, {
      method: 'POST',
      headers: this.billingHeaders(),
      body: JSON.stringify({ confirm_phrase: confirmPhrase, cancel_at_gateway: cancelAtGateway }),
    });
  }

  /** Ask the concierge to approach a TTO. The user is not cc'd by design. */
  async submitConciergeRequest(payload: {
    tech_id: string; tech_title?: string; university?: string;
    contact_name: string; contact_email: string; cc_emails?: string[]; note?: string;
  }): Promise<ApiResponse<any>> {
    return this.makeRequest('/api/v1/concierge/request', {
      method: 'POST', headers: this.billingHeaders(),
      body: JSON.stringify({ user_id: this.getStoredUserId() || '', payload }),
    });
  }

  async adminConciergeRequests(userId: string, limit = 100): Promise<ApiResponse<any>> {
    return this.makeRequest(
      `/api/v1/admin/concierge/requests?user_id=${encodeURIComponent(userId)}&limit=${limit}`,
      { headers: this.billingHeaders() });
  }

  async adminSetConciergeCc(userId: string, emails: string[]): Promise<ApiResponse<any>> {
    return this.makeRequest(`/api/v1/admin/concierge/cc?user_id=${encodeURIComponent(userId)}`, {
      method: 'POST', headers: this.billingHeaders(), body: JSON.stringify(emails),
    });
  }

  async adminRazorpayMode(userId: string): Promise<ApiResponse<any>> {
    return this.makeRequest(`/api/v1/admin/billing/razorpay-mode?user_id=${encodeURIComponent(userId)}`, {
      headers: this.billingHeaders(),
    });
  }

  /** `confirm` is required by the server when switching to live. */
  async adminSetRazorpayMode(userId: string, mode: 'test' | 'live',
                             confirm = false): Promise<ApiResponse<any>> {
    return this.makeRequest(`/api/v1/admin/billing/razorpay-mode?user_id=${encodeURIComponent(userId)}`, {
      method: 'POST',
      headers: this.billingHeaders(),
      body: JSON.stringify({ mode, confirm }),
    });
  }

  /** Billing → Emails: every e-mail a payment event sends, with previews. */
  async adminBillingEmails(userId: string): Promise<ApiResponse<any>> {
    return this.makeRequest(
      `/api/v1/admin/billing/emails?user_id=${encodeURIComponent(userId)}`,
      { headers: this.billingHeaders() });
  }

  /** Billing → Payments → Failed attempts: failures with their cause in words. */
  async adminBillingFailedAttempts(userId: string, days = 30): Promise<ApiResponse<any>> {
    return this.makeRequest(
      `/api/v1/admin/billing/failed-attempts?user_id=${encodeURIComponent(userId)}&days=${days}`,
      { headers: this.billingHeaders() });
  }

  async adminBillingWebhookEvents(userId: string, limit = 100): Promise<ApiResponse<any>> {
    return this.makeRequest(
      `/api/v1/admin/billing/webhook-events?user_id=${encodeURIComponent(userId)}&limit=${limit}`,
      { headers: this.billingHeaders() });
  }

  async adminBillingOverview(userId: string, search = '', page = 0, perPage = 10): Promise<ApiResponse<any>> {
    return this.makeRequest(
      `/api/v1/admin/billing/overview?user_id=${encodeURIComponent(userId)}`
      + `&search=${encodeURIComponent(search)}&page=${page}&per_page=${perPage}`,
      { headers: this.billingHeaders() });
  }

  async adminBillingSetGlobalPayOnce(userId: string, allow: boolean): Promise<ApiResponse<any>> {
    return this.makeRequest(`/api/v1/admin/billing/settings?user_id=${encodeURIComponent(userId)}`, {
      method: 'POST', headers: this.billingHeaders(),
      body: JSON.stringify({ allow_one_time_all: allow }),
    });
  }

  async billingReceipt(userId: string, paymentId: string): Promise<ApiResponse<any>> {
    return this.makeRequest(
      `/api/v1/billing/receipt/${encodeURIComponent(paymentId)}?user_id=${encodeURIComponent(userId)}`,
      { headers: this.billingHeaders() });
  }

  async adminBillingPlans(userId: string): Promise<ApiResponse<any>> {
    return this.makeRequest(`/api/v1/admin/billing/plans?user_id=${encodeURIComponent(userId)}`, {
      headers: this.billingHeaders(),
    });
  }

  async adminBillingCreatePlan(userId: string, fields: Record<string, any>): Promise<ApiResponse<any>> {
    return this.makeRequest(`/api/v1/admin/billing/plans?user_id=${encodeURIComponent(userId)}`, {
      method: 'POST', headers: this.billingHeaders(), body: JSON.stringify({ fields }),
    });
  }

  /** Refused server-side while any subscription still references the plan. */
  async adminBillingDeletePlan(userId: string, code: string): Promise<ApiResponse<any>> {
    return this.makeRequest(
      `/api/v1/admin/billing/plans/${encodeURIComponent(code)}`
      + `?user_id=${encodeURIComponent(userId)}&confirm=true`,
      { method: 'DELETE', headers: this.billingHeaders() });
  }

  async adminBillingUpdatePlan(userId: string, code: string, fields: Record<string, any>): Promise<ApiResponse<any>> {
    return this.makeRequest(`/api/v1/admin/billing/plans/${encodeURIComponent(code)}?user_id=${encodeURIComponent(userId)}`, {
      method: 'PUT', headers: this.billingHeaders(), body: JSON.stringify(fields),
    });
  }

  async adminBillingManualSub(userId: string, payload: { email: string; plan_code: string; months: number; note: string }): Promise<ApiResponse<any>> {
    return this.makeRequest(`/api/v1/admin/billing/manual-subscription?user_id=${encodeURIComponent(userId)}`, {
      method: 'POST', headers: this.billingHeaders(), body: JSON.stringify(payload),
    });
  }

  /** Billing → Payments → Record a manual payment: the account, its live
   *  membership and (for a pay_ id) what Razorpay says about the payment. */
  async adminManualPaymentPreview(userId: string, email: string, razorpayPaymentId = ''): Promise<ApiResponse<any>> {
    return this.makeRequest(
      `/api/v1/admin/billing/manual-payment/preview?user_id=${encodeURIComponent(userId)}`
      + `&email=${encodeURIComponent(email)}&razorpay_payment_id=${encodeURIComponent(razorpayPaymentId)}`,
      { headers: this.billingHeaders() });
  }

  /** Captured Razorpay payments no account holds, with a suggested account. */
  async adminUnmatchedPayments(userId: string, days = 90): Promise<ApiResponse<any>> {
    return this.makeRequest(
      `/api/v1/admin/billing/manual-payment/unmatched?user_id=${encodeURIComponent(userId)}&days=${days}`,
      { headers: this.billingHeaders() });
  }

  async adminHideUnmatchedPayment(userId: string, paymentId: string, hide = true): Promise<ApiResponse<any>> {
    return this.makeRequest(`/api/v1/admin/billing/manual-payment/hide?user_id=${encodeURIComponent(userId)}`, {
      method: 'POST', headers: this.billingHeaders(), body: JSON.stringify({ payment_id: paymentId, hide }),
    });
  }

  async adminRecordManualPayment(userId: string, body: Record<string, any>): Promise<ApiResponse<any>> {
    return this.makeRequest(`/api/v1/admin/billing/manual-payment?user_id=${encodeURIComponent(userId)}`, {
      method: 'POST', headers: this.billingHeaders(), body: JSON.stringify(body),
    });
  }

  async adminBillingSetUserFlag(userId: string, targetUserId: string, allowOneTime: boolean): Promise<ApiResponse<any>> {
    return this.makeRequest(`/api/v1/admin/billing/user-flags?user_id=${encodeURIComponent(userId)}`, {
      method: 'POST', headers: this.billingHeaders(),
      body: JSON.stringify({ target_user_id: targetUserId, allow_one_time: allowOneTime }),
    });
  }

  /** `reason` is the admin's own note on the cancellation row (its
   *  other_text); the customer-facing sheet is what collects reasons from the
   *  user, so here it is optional. Always sent, even blank, so the body keeps
   *  two keys — the route's lone-scalar embed quirk is explained on it. */
  async adminBillingCancelSub(userId: string, targetUserId: string,
                              reason = ''): Promise<ApiResponse<any>> {
    return this.makeRequest(`/api/v1/admin/billing/cancel-subscription?user_id=${encodeURIComponent(userId)}`, {
      method: 'POST', headers: this.billingHeaders(),
      body: JSON.stringify({ target_user_id: targetUserId, reason }),
    });
  }

  /** Move an account onto a cheaper plan. `immediate` ends paid access now;
   *  otherwise they keep the time already paid for. A refund is separate and
   *  only happens when refundPaymentId is given. */
  async adminBillingDowngrade(userId: string, targetUserId: string, toPlanCode: string,
                              immediate = false, refundPaymentId?: string,
                              note = ''): Promise<ApiResponse<any>> {
    return this.makeRequest(
      `/api/v1/admin/billing/downgrade?user_id=${encodeURIComponent(userId)}`,
      { method: 'POST', headers: this.billingHeaders(),
        body: JSON.stringify({
          target_user_id: targetUserId, to_plan_code: toPlanCode,
          immediate, refund_payment_id: refundPaymentId || null, note,
        }) });
  }

  /** Set the country billing reads for an account. Blank hands the decision
   *  back to geo detection (edge header, then IP, then India). */
  async adminBillingSetCountry(userId: string, targetUserId: string,
                               country: string): Promise<ApiResponse<any>> {
    return this.makeRequest(
      `/api/v1/admin/billing/user-country?user_id=${encodeURIComponent(userId)}`,
      { method: 'POST', headers: this.billingHeaders(),
        body: JSON.stringify({ target_user_id: targetUserId, country }) });
  }

  /** Absolute URL of the analytics beacon, for navigator.sendBeacon — which
   *  needs a URL rather than going through makeRequest. */
  trackUrl(): string {
    return `${this.baseUrl}/api/v1/track`;
  }

  /** First-party visit beacon. Public, unauthenticated, and deliberately
   *  fire-and-forget: a failed beacon must never reach the visitor. */
  async track(body: Record<string, unknown>): Promise<void> {
    try {
      await fetch(this.trackUrl(), {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body), keepalive: true,
      });
    } catch { /* ignore */ }
  }

  /** Countries an admin may set on an account, each with the currency it
   *  bills in. */
  async adminBillingCountries(userId: string): Promise<ApiResponse<any>> {
    return this.makeRequest(
      `/api/v1/admin/billing/countries?user_id=${encodeURIComponent(userId)}`,
      { headers: this.billingHeaders() });
  }

  /** The region map: which countries bill in which currency. */
  async adminBillingRegions(userId: string): Promise<ApiResponse<any>> {
    return this.makeRequest(`/api/v1/admin/billing/regions?user_id=${encodeURIComponent(userId)}`,
      { headers: this.billingHeaders() });
  }

  async adminBillingSetRegions(userId: string, regions: any[]): Promise<ApiResponse<any>> {
    return this.makeRequest(`/api/v1/admin/billing/regions?user_id=${encodeURIComponent(userId)}`, {
      // The route declares Body(..., embed=True), so the list has to arrive
      // wrapped under its own key rather than as a bare array.
      method: 'POST', headers: this.billingHeaders(), body: JSON.stringify({ regions }),
    });
  }

  /** Every region's price for one plan, with the FX comparison beside it. */
  async adminBillingPlanPrices(userId: string, code: string): Promise<ApiResponse<any>> {
    return this.makeRequest(
      `/api/v1/admin/billing/plans/${encodeURIComponent(code)}/prices?user_id=${encodeURIComponent(userId)}`,
      { headers: this.billingHeaders() });
  }

  /** Set one region's price. `amount_major` is what a person types (89, not
   *  8900) — the server converts using that currency's own minor unit, which
   *  is not 100 everywhere. */
  async adminBillingSetPlanPrice(userId: string, code: string, region: string,
                                 currency: string, amountMajor: number): Promise<ApiResponse<any>> {
    return this.makeRequest(
      `/api/v1/admin/billing/plans/${encodeURIComponent(code)}/prices/${encodeURIComponent(region)}`
      + `?user_id=${encodeURIComponent(userId)}`, {
      method: 'PUT', headers: this.billingHeaders(),
      body: JSON.stringify({ currency, amount_major: amountMajor }),
    });
  }

  async adminBillingDeletePlanPrice(userId: string, code: string,
                                    region: string): Promise<ApiResponse<any>> {
    return this.makeRequest(
      `/api/v1/admin/billing/plans/${encodeURIComponent(code)}/prices/${encodeURIComponent(region)}`
      + `?user_id=${encodeURIComponent(userId)}`,
      { method: 'DELETE', headers: this.billingHeaders() });
  }

  /** Copy the legacy single-price columns into the region table. Idempotent
   *  and non-destructive — it never overwrites a row that exists. */
  async adminBillingSeedRegionPrices(userId: string): Promise<ApiResponse<any>> {
    return this.makeRequest(
      `/api/v1/admin/billing/plans/seed-region-prices?user_id=${encodeURIComponent(userId)}`,
      { method: 'POST', headers: this.billingHeaders() });
  }

  /** Indicative conversion rates for the price form. Advisory only: the charge
   *  is whatever sits on the gateway plan in its own currency. */
  async adminBillingFx(userId: string, base = 'INR', refresh = false): Promise<ApiResponse<any>> {
    return this.makeRequest(
      `/api/v1/admin/billing/fx?base=${encodeURIComponent(base)}&refresh=${refresh ? 'true' : 'false'}`
      + `&user_id=${encodeURIComponent(userId)}`, { headers: this.billingHeaders() });
  }

  async adminBillingCoupons(userId: string): Promise<ApiResponse<any>> {
    return this.makeRequest(`/api/v1/admin/billing/coupons?user_id=${encodeURIComponent(userId)}`, {
      headers: this.billingHeaders(),
    });
  }

  async adminBillingSaveCoupon(userId: string, payload: Record<string, any>): Promise<ApiResponse<any>> {
    return this.makeRequest(`/api/v1/admin/billing/coupons?user_id=${encodeURIComponent(userId)}`, {
      method: 'POST', headers: this.billingHeaders(), body: JSON.stringify(payload),
    });
  }

  /** `confirm` is required by the server once a code has been redeemed,
   *  since deleting it loses the record of what the code meant. */
  /** Public directory for /universities, built from the corpus and cached
   *  server-side (rebuilt on restart, then weekly). Callers should fall back
   *  to the bundled analytics_pie.json snapshot if this is unreachable. */
  async universitiesDirectory(): Promise<ApiResponse<any>> {
    return this.makeRequest('/api/v1/universities/directory');
  }

  /** Log that someone pressed a booking CTA. Unauthenticated on purpose —
   *  these CTAs appear on signed-out surfaces, and a lead we cannot record is
   *  worse than one recorded without a session. */
  async recordInterestClick(source: string,
                            context: Record<string, any> = {}): Promise<ApiResponse<any>> {
    return this.makeRequest('/api/v1/interest/click', {
      method: 'POST',
      body: JSON.stringify({
        source,
        user_id: this.getStoredUserId(),   // server resolves name/email from this
        context,
      }),
    });
  }

  /** One-time voucher codes. Each code is its own credential, so a discount
   *  can be sent to a named person without knowing which address they will
   *  sign up with. */
  async adminBillingVouchers(userId: string, promoId = ''): Promise<ApiResponse<any>> {
    return this.makeRequest(
      `/api/v1/admin/billing/vouchers?user_id=${encodeURIComponent(userId)}`
      + `&promo_id=${encodeURIComponent(promoId)}`,
      { headers: this.billingHeaders() });
  }

  async adminBillingGenerateVouchers(userId: string, promoId: string, count: number,
                                     prefix = '', issuedTo?: string[]): Promise<ApiResponse<any>> {
    return this.makeRequest(
      `/api/v1/admin/billing/vouchers?user_id=${encodeURIComponent(userId)}`,
      { method: 'POST', headers: this.billingHeaders(),
        body: JSON.stringify({ promo_id: promoId, count, prefix,
                               issued_to: issuedTo || null }) });
  }

  async adminBillingDeleteVoucher(userId: string, code: string): Promise<ApiResponse<any>> {
    return this.makeRequest(
      `/api/v1/admin/billing/vouchers/${encodeURIComponent(code)}`
      + `?user_id=${encodeURIComponent(userId)}`,
      { method: 'DELETE', headers: this.billingHeaders() });
  }

  /** Offers defined in the Razorpay dashboard. They cannot be created over
   *  the API, so a promotion attaches to one by picking it here. */
  async adminBillingRazorpayOffers(userId: string): Promise<ApiResponse<any>> {
    return this.makeRequest(
      `/api/v1/admin/billing/razorpay-offers?user_id=${encodeURIComponent(userId)}`,
      { headers: this.billingHeaders() });
  }

  async adminBillingDeleteCoupon(userId: string, code: string,
                                 confirm = false): Promise<ApiResponse<any>> {
    return this.makeRequest(
      `/api/v1/admin/billing/coupons/${encodeURIComponent(code)}`
      + `?user_id=${encodeURIComponent(userId)}&confirm=${confirm}`,
      { method: 'DELETE', headers: this.billingHeaders() });
  }

  /** The admin e-mails, read only (IP Intel → order assignee picker). Admins
   *  are added, changed and removed in Admins & access only (panel section "admins"). */
  async adminBillingAdmins(userId: string): Promise<ApiResponse<any>> {
    return this.makeRequest(`/api/v1/admin/billing/admins?user_id=${encodeURIComponent(userId)}`, {
      headers: this.billingHeaders(),
    });
  }

  /** Admin: give one account a trial regardless of the global switch. */
  async adminBillingGrantTrial(userId: string, targetUserId: string,
                               days = 0): Promise<ApiResponse<any>> {
    return this.makeRequest(
      `/api/v1/admin/billing/grant-trial?user_id=${encodeURIComponent(userId)}`, {
      method: 'POST', headers: this.billingHeaders(),
      body: JSON.stringify({ target_user_id: targetUserId, days }),
    });
  }

  /** The card-free trial — the default since the Sep 2026 spec. The four
   *  details are what the trial form collects; the server refuses without
   *  them (400 naming the missing field), writes them to the profile and
   *  keeps a snapshot on the trial row. The legacy autopay trial goes through
   *  billingSubscribe with trial=true, because it is a real subscription with
   *  a deferred charge. */
  async billingStartTrial(userId: string, form: TrialForm, origin?: string): Promise<ApiResponse<any>> {
    // `origin` (e.g. 'onboarding_picks_block', 'paywall:search') lands on the
    // trial row's form snapshot for attribution; older servers ignore it.
    return this.makeRequest('/api/v1/billing/trial/start', {
      method: 'POST', headers: this.billingHeaders(),
      body: JSON.stringify({
        user_id: userId,
        first_name: form.first_name, last_name: form.last_name,
        organization: form.organization, designation: form.designation,
        ...(origin ? { origin } : {}),
      }),
    });
  }

  async adminBillingPromotions(userId: string): Promise<ApiResponse<any>> {
    return this.makeRequest(`/api/v1/admin/billing/promotions?user_id=${encodeURIComponent(userId)}`,
      { headers: this.billingHeaders() });
  }

  async adminBillingSavePromotion(userId: string, payload: Record<string, any>): Promise<ApiResponse<any>> {
    return this.makeRequest(`/api/v1/admin/billing/promotions?user_id=${encodeURIComponent(userId)}`, {
      method: 'POST', headers: this.billingHeaders(), body: JSON.stringify(payload),
    });
  }

  async adminBillingDeletePromotion(userId: string, promoId: string,
                                    confirm = false): Promise<ApiResponse<any>> {
    return this.makeRequest(
      `/api/v1/admin/billing/promotions/${encodeURIComponent(promoId)}`
      + `?user_id=${encodeURIComponent(userId)}&confirm=${confirm}`,
      { method: 'DELETE', headers: this.billingHeaders() });
  }

  /** `require_mandate` is the legacy autopay trial (card up front, first
   *  charge at trial end). Off by default: the standard trial is card-free
   *  and simply lapses. */
  async adminBillingTrialSettings(userId: string, payload: {
    enabled: boolean; days: number; plan_code: string; require_mandate?: boolean;
  }): Promise<ApiResponse<any>> {
    return this.makeRequest(`/api/v1/admin/billing/trial-settings?user_id=${encodeURIComponent(userId)}`, {
      method: 'POST', headers: this.billingHeaders(), body: JSON.stringify(payload),
    });
  }

  async adminBillingExtend(userId: string, targetUserId: string, days: number): Promise<ApiResponse<any>> {
    return this.makeRequest(`/api/v1/admin/billing/extend?user_id=${encodeURIComponent(userId)}`, {
      method: 'POST', headers: this.billingHeaders(),
      body: JSON.stringify({ target_user_id: targetUserId, days }),
    });
  }

  async adminBillingCapabilities(userId: string, recheck = false): Promise<ApiResponse<any>> {
    return this.makeRequest(
      `/api/v1/admin/billing/capabilities?user_id=${encodeURIComponent(userId)}&recheck=${recheck}`,
      { headers: this.billingHeaders() });
  }

  async adminBillingRefund(userId: string, payload: {
    payment_id: string; amount_minor?: number | null; reason?: string; end_access?: boolean;
  }): Promise<ApiResponse<any>> {
    return this.makeRequest(`/api/v1/admin/billing/refund?user_id=${encodeURIComponent(userId)}`, {
      method: 'POST', headers: this.billingHeaders(), body: JSON.stringify(payload),
    });
  }

  async adminBillingPayments(userId: string): Promise<ApiResponse<any>> {
    return this.makeRequest(`/api/v1/admin/billing/payments?user_id=${encodeURIComponent(userId)}`, {
      headers: this.billingHeaders(),
    });
  }

  // ---- Orphaned billing (money on a user id that no longer exists) ------
  // A trial mandate charged after its account was deleted and re-created
  // (2026-09-20): the payment, its subscription and the webhooks all carry
  // the old id, so nothing in the panel could say who paid. These read the
  // rows grouped by ghost id, with the payer identity the webhook payload
  // carried, and move them onto the account that now holds that e-mail.

  /** {rows: [{user_id (ghost), payer_email, payer_contact, payments,
   *  amount_by_currency, subscriptions, live_mandates, last_at,
   *  suggested_user: {id, email, name} | null}]} */
  async adminBillingOrphans(userId: string): Promise<ApiResponse<any>> {
    return this.makeRequest(`/api/v1/admin/billing/orphans?user_id=${encodeURIComponent(userId)}`, {
      headers: this.billingHeaders(),
    });
  }

  /** Re-key every billing row of `ghostUserId` onto `targetUserId`. The
   *  server refuses unless the ghost really has no users row and the target
   *  really has one, so a typo cannot move a live customer's history. */
  async adminBillingReattribute(userId: string, ghostUserId: string,
                                targetUserId: string): Promise<ApiResponse<any>> {
    return this.makeRequest(`/api/v1/admin/billing/reattribute?user_id=${encodeURIComponent(userId)}`, {
      method: 'POST', headers: this.billingHeaders(),
      body: JSON.stringify({ ghost_user_id: ghostUserId, target_user_id: targetUserId }),
    });
  }

  // ---- Refunds from the Users tab ----------------------------------------
  // The Payments tab refunds by payment id; the Users tab refunds a PERSON,
  // so it first asks what they have paid that can still come back.

  /** {payments: [refundable payments of the user, newest first, each with
   *  refunded_so_far], subscription: the active row or null} */
  async adminBillingUserPayments(userId: string, targetUserId: string): Promise<ApiResponse<any>> {
    return this.makeRequest(
      `/api/v1/admin/billing/user-payments?user_id=${encodeURIComponent(userId)}`
      + `&target_user_id=${encodeURIComponent(targetUserId)}`,
      { headers: this.billingHeaders() });
  }

  /** Omit payment_id for the newest refundable payment, amount_minor for a
   *  full refund. Answers with the Razorpay refund id and refund_log_id. */
  async adminBillingRefundUser(userId: string, payload: {
    target_user_id: string; payment_id?: string | null; amount_minor?: number | null;
    reason?: string; end_access?: boolean;
  }): Promise<ApiResponse<any>> {
    return this.makeRequest(`/api/v1/admin/billing/refund-user?user_id=${encodeURIComponent(userId)}`, {
      method: 'POST', headers: this.billingHeaders(), body: JSON.stringify(payload),
    });
  }

  /** The refunds log (billing_refunds joined with users), newest first —
   *  issued and failed alike, because a failed refund is the one somebody
   *  has to chase. */
  async adminBillingRefunds(userId: string, limit = 200): Promise<ApiResponse<any>> {
    return this.makeRequest(
      `/api/v1/admin/billing/refunds?user_id=${encodeURIComponent(userId)}&limit=${limit}`, {
      headers: this.billingHeaders(),
    });
  }

  // ---- The Razorpay tab -------------------------------------------------
  // What the GATEWAY holds, rather than what our tables do: a mandate on an
  // account that no longer exists (2026-09-20) is invisible to every other
  // admin read, and was found in the Razorpay dashboard. Each row comes
  // back joined to ours (`our_row`, null when we hold nothing), so the tab
  // can say "not in our DB" where the dashboard cannot.

  /** One of the three gateway lists. `params` are the route's query
   *  params: subscriptions take status (active | authenticated | pending |
   *  halted | cancelled | completed | all), payments take from_ts / to_ts
   *  (epoch seconds); all take count / skip, and refresh=true bypasses the
   *  server's 60 s cache. Answers {mode, rows, count, fetched_at, error?} —
   *  a gateway failure is `error` with success still true. */
  async adminBillingGateway(userId: string, kind: 'subscriptions' | 'payments' | 'refunds',
                            params: Record<string, string | number | boolean | null | undefined> = {}):
    Promise<ApiResponse<any>> {
    const q = new URLSearchParams({ user_id: userId });
    Object.entries(params).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== '') q.set(k, String(v));
    });
    return this.makeRequest(`/api/v1/admin/billing/gateway/${kind}?${q.toString()}`, {
      headers: this.billingHeaders(),
    });
  }

  /** Stop a mandate at Razorpay NOW by its gateway id (sub_…). Works with or
   *  without a row on our side; the reason is required by the server. */
  async adminBillingGatewayCancel(userId: string, subscriptionId: string,
                                  reason: string): Promise<ApiResponse<any>> {
    return this.makeRequest(
      `/api/v1/admin/billing/gateway/cancel-subscription?user_id=${encodeURIComponent(userId)}`, {
      method: 'POST', headers: this.billingHeaders(),
      body: JSON.stringify({ subscription_id: subscriptionId, reason }),
    });
  }

  /** Refund by Razorpay payment id (pay_…). With a billing_payments row it
   *  is the ordinary refund; without one it is made at the gateway directly
   *  and, on a full refund, the mandate behind it is cancelled. Omit
   *  amount_minor for a full refund. The reason is required. */
  async adminBillingGatewayRefund(userId: string, payload: {
    payment_id: string; amount_minor?: number | null; reason: string; end_access?: boolean;
  }): Promise<ApiResponse<any>> {
    return this.makeRequest(`/api/v1/admin/billing/gateway/refund?user_id=${encodeURIComponent(userId)}`, {
      method: 'POST', headers: this.billingHeaders(), body: JSON.stringify(payload),
    });
  }

  async adminBillingTrialCancellations(userId: string, limit = 200): Promise<ApiResponse<any>> {
    return this.makeRequest(
      `/api/v1/admin/billing/trial-cancellations?user_id=${encodeURIComponent(userId)}&limit=${limit}`, {
      headers: this.billingHeaders(),
    });
  }

  /** Everything the Trials tab shows in one read: {funnel, cancellations,
   *  events, trials}. One call rather than four because the numbers are read
   *  against each other — "opened" only means something next to "kept".
   *  `segment` narrows the trial rows by lifecycle state ('all' | 'active' |
   *  'lapsed' | 'converted' | 'cancelled'); the funnel and logs are unchanged
   *  by it. Sent only when narrowed, so an older backend sees the same URL. */
  async adminBillingTrials(userId: string, limit = 200,
                           segment: TrialSegment = 'all'): Promise<ApiResponse<any>> {
    const seg = segment && segment !== 'all' ? `&segment=${encodeURIComponent(segment)}` : '';
    return this.makeRequest(
      `/api/v1/admin/billing/trials?user_id=${encodeURIComponent(userId)}&limit=${limit}${seg}`, {
      headers: this.billingHeaders(),
    });
  }

  // ---- Direct Search history (server-persisted, per-user) ----
  async saveDirectSearchHistory(
    userId: string,
    payload: {
      query: string; filters: DirectSearchFilters;
      /** The real method (searchMethodLabel), e.g. "v2:rag_pinecone:engine". */
      backend: DirectSearchBackend | string;
      total: number;
    },
  ): Promise<ApiResponse<{ entry: DirectSearchHistoryEntry }>> {
    return this.makeRequest('/api/v1/direct-search/history', {
      method: 'POST',
      body: JSON.stringify({
        user_id: userId,
        query: payload.query,
        filters: payload.filters,
        backend: payload.backend,
        total: payload.total,
      }),
    });
  }

  async listDirectSearchHistory(
    userId: string,
    limit: number = 30,
  ): Promise<ApiResponse<{ entries: DirectSearchHistoryEntry[] }>> {
    const qp = new URLSearchParams({ user_id: userId, limit: String(limit) });
    return this.makeRequest(`/api/v1/direct-search/history?${qp.toString()}`, { method: 'GET' });
  }

  async deleteDirectSearchHistoryEntry(
    userId: string,
    entryId: string,
  ): Promise<ApiResponse<{ success: boolean }>> {
    const qp = new URLSearchParams({ user_id: userId });
    return this.makeRequest(`/api/v1/direct-search/history/${encodeURIComponent(entryId)}?${qp.toString()}`, {
      method: 'DELETE',
    });
  }

  async clearDirectSearchHistory(
    userId: string,
  ): Promise<ApiResponse<{ deleted: number }>> {
    const qp = new URLSearchParams({ user_id: userId });
    return this.makeRequest(`/api/v1/direct-search/history?${qp.toString()}`, { method: 'DELETE' });
  }

  // ============================================================
  // Homepage v2 — public/unauthenticated endpoints used by the
  // new public landing page (NewHome.tsx + landing-page App.tsx).
  // ============================================================
  async getHomepageStats(): Promise<ApiResponse<{ stats: HomepageStats; cached?: boolean }>> {
    return this.makeRequest('/api/v1/homepage/stats', { method: 'GET' });
  }

  /** The admin-edited homepage blocks (Admin → Homepage): the "Trusted by"
   *  logo banner and the Success Stories rows. `content` is null when the
   *  server could not build it; the homepage then keeps its built-in copy. */
  async getHomepageContent(): Promise<ApiResponse<{ success: boolean; content: HomepageContent | null; error?: string }>> {
    return this.makeRequest('/api/v1/homepage/content', { method: 'GET' });
  }

  async getHomepageTopUniversities(
    limit: number = 15,
  ): Promise<ApiResponse<{ universities: HomepageTopUniversity[]; cached?: boolean }>> {
    return this.makeRequest(`/api/v1/homepage/top-universities?limit=${limit}`, { method: 'GET' });
  }

  async submitUniversityLead(payload: {
    full_name: string; email: string; designation: string; university_name: string;
    /** Free-text form identifier so leads can be routed by origin
     * (e.g. "list_your_ip_homepage", "list_your_ip_discover_section"). */
    form_source?: string;
  }): Promise<ApiResponse<{ success: boolean }>> {
    return this.makeRequest('/api/v1/homepage/list-your-ip', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  }

  /**
   * Early-access signup capture (OCEAN Blue / OCEAN Search / future products).
   * Writes to the unified `early_access_signups` table on the backend so all
   * pre-launch interest can be filtered by `source`.
   */
  async submitEarlyAccess(payload: {
    full_name: string;
    email: string;
    designation: string;
    university_name: string;
    source: 'ocean_blue' | 'ocean_search' | 'cosmos_search' | 'list_your_ip';
    /** Optional free-text label for finer attribution. */
    form_source?: string;
  }): Promise<ApiResponse<{ success: boolean }>> {
    return this.makeRequest('/api/v1/homepage/early-access', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  }

  async submitNewsletterSignup(email: string, formSource: string = 'homepage_footer_newsletter'): Promise<ApiResponse<{ success: boolean; already_subscribed?: boolean }>> {
    return this.makeRequest('/api/v1/homepage/newsletter', {
      method: 'POST',
      body: JSON.stringify({ email, form_source: formSource }),
    });
  }

  /**
   * Request the R&D Symposium whitepaper. The backend sends the PDF as a
   * SendGrid attachment to the supplied email address and writes a row to
   * `whitepaper_requests` for ops visibility.
   */
  async submitWhitepaperRequest(data: {
    name: string;
    email: string;
    organisation?: string;
    form_source?: string;
  }): Promise<ApiResponse<{ success: boolean }>> {
    return this.makeRequest('/api/v1/homepage/whitepaper', {
      method: 'POST',
      body: JSON.stringify({
        name: data.name,
        email: data.email,
        organisation: data.organisation || '',
        form_source: data.form_source || 'rd_symposium_whitepaper',
      }),
    });
  }

  /**
   * COMET Phase 1 expression of interest (public /comet page).
   *
   * The backend re-validates every field and, on rejection, returns
   * `{ success: false, error, field }` with a 400 so the form can highlight
   * the offending input. Counts are sent as strings because the inputs are
   * text (numeric keypad on mobile); the server coerces them.
   */
  async submitCometInterest(data: {
    work_email: string;
    full_name: string;
    designation: string;
    organisation: string;
    phone: string;
    organisation_type: string;
    organisation_type_other?: string;
    pre_revenue?: string | number;
    pilots_underway?: string | number;
    early_commercial?: string | number;
    established_revenue?: string | number;
    data_points: string[];
    form_source?: string;
  }): Promise<ApiResponse<{
    success: boolean;
    reference_code?: string;
    message?: string;
    error?: string;
    field?: string;
  }>> {
    return this.makeRequest('/api/v1/comet/express-interest', {
      method: 'POST',
      body: JSON.stringify({
        work_email: data.work_email,
        full_name: data.full_name,
        designation: data.designation,
        organisation: data.organisation,
        phone: data.phone,
        organisation_type: data.organisation_type,
        organisation_type_other: data.organisation_type_other || '',
        pre_revenue: data.pre_revenue ?? 0,
        pilots_underway: data.pilots_underway ?? 0,
        early_commercial: data.early_commercial ?? 0,
        established_revenue: data.established_revenue ?? 0,
        data_points: data.data_points,
        form_source: data.form_source || 'comet_phase1_eoi',
      }),
    });
  }
}

export interface HomepageStats {
  universities: number;
  universities_new_this_month: number;
  tech_offers: number;
  tech_offers_new_this_month: number;
  countries: number;
  countries_new_this_month: number;
}

/** One logo in the homepage "Trusted by" banner. `logo` is '' (show the name
 *  as text), a site path ('/homepage/logos/x.png'), an uploaded logo on the
 *  API ('/api/v1/site-assets/<id>' — prefix the API base URL) or an https URL. */
export interface HomepageTrustedItem {
  id: string;
  name: string;
  logo: string;
}

/** One Success Stories row: "<from> — <verb> — <to>". */
export interface HomepageStoryRow {
  from: string;
  verb: string;
  to: string;
}

/** GET /api/v1/homepage/content → content. Only enabled logos are sent, in
 *  display order. */
export interface HomepageContent {
  version: string;
  trusted: {
    enabled: boolean;
    title: string;
    /** Under the title; '' hides it. Missing from an API older than
     *  2026-10-14 — the page then uses its built-in line. */
    subtitle?: string;
    /** cards = a grid of logo cards (the default since 2026-10-14); rows /
     *  scroll = the banner, which ends with `more_text`. */
    layout: 'cards' | 'rows' | 'scroll';
    more_text: string;
    items: HomepageTrustedItem[];
  };
  stories: {
    enabled: boolean;
    title: string;
    subtitle: string;
    rows: HomepageStoryRow[];
  };
  /** Search chips under the homepage box and the Surprise Me pool. Optional:
   *  an older API does not send it, and the page then keeps its own lists. */
  search?: {
    enabled: boolean;
    bubbles: string[];
    surprise: string[];
    surprise_label?: string;
  };
}

export interface HomepageTopUniversity {
  name: string;
  full_name?: string;
  country: string;
  count: number;
}

export type DirectSearchBackend = 'hybrid' | 'algolia' | 'native';

/** Which homepage control ran a search: the box, a suggestion chip, Surprise Me. */
export type HomeSearchSource = 'typed' | 'chip' | 'surprise';

/** POST /api/v1/home/search → data. `results` are the same objects
 *  /direct-search/algolia returns; `total` is the engine's full match count
 *  (only 3 are shown); `search_id` is the search_query_logs row, null when the
 *  server could not log it. */
export interface HomeSearchData {
  results: any[];
  total: number;
  engine: 'typesense' | 'algolia-fallback' | string;
  took_ms: number;
  search_id: string | null;
}

export interface DirectSearchFilters {
  countries?: string[];
  university_codes?: string[];
  university_names?: string[];
  major_categories?: string[];
  // trl_levels (multi-select) takes precedence over min/max range when both present.
  trl_levels?: number[];
  trl_min?: number | null;
  trl_max?: number | null;
}

export interface DirectSearchFacets {
  countries: string[];
  countries_with_counts?: { value: string; count: number }[];
  universities: { name: string; code: string; country: string; count?: number }[];
  major_categories: string[];
  major_categories_with_counts?: { value: string; count: number }[];
  trl_levels?: { value: number; label: string; count: number }[];
  trl_min: number;
  trl_max: number;
  /** Corpus size — lets the UI derive the "Not disclosed" TRL count. */
  total_technologies?: number;
}

export interface DirectSearchRequest {
  query: string;
  filters?: DirectSearchFilters;
  page?: number;
  hits_per_page?: number;
  /**
   * Attributed to the row written in `search_query_logs`. Without it the
   * hybrid debug logs cannot be tied back to a user, which is most of their
   * value when investigating "my search returned nothing".
   */
  user_id?: string;
  /**
   * AI-extracted filter values the user explicitly unchecked. The backend
   * strips these from the AI plan before applying filters — otherwise the
   * re-run would just re-extract the phrase and re-apply the filter, and the
   * uncheck could never win.
   */
  ai_exclude?: {
    trl_levels?: number[];
    countries?: string[];
    university_names?: string[];
  };
  /**
   * search_v2 only. 'engine' is the "Run deep search" button: skip the
   * simple/complex classification and send the query through the configured
   * RAG engine (LLM rescore included) even though it would have routed to the
   * fast keyword path. Omitted (never null) for a normal search so the body
   * stays byte-identical to what the legacy endpoint has always received.
   */
  force_route?: 'engine';
  /** "Search anyway" after the query was flagged as nonsense. */
  allow_nonsense?: boolean;
  /** search_v2: skip the remembered plan decision (sent once, right after an upgrade). */
  fresh_entitlements?: boolean;
}

export interface DirectSearchFacetCounts {
  // Maps Algolia/native facet attribute → { value: count }
  university_country?: Record<string, number>;
  utech_university_id?: Record<string, number>;
  calculated_trl?: Record<string, number>;
  major_industry_category?: Record<string, number>;
  major_categories?: Record<string, number>;
}

/**
 * Diagnostics block search_v2 adds to the legacy envelope. Everything is
 * optional: the legacy endpoint has no `meta` at all, and v2 engines that
 * skip a stage (e.g. no LLM rescore) leave its fields out.
 */
export interface SearchV2Meta {
  /** 'typesense' | 'sql' (simple keyword routes) | 'engine' (configured RAG engine). */
  route?: string;
  engine?: string;
  classification?: {
    label?: string; reason?: string; ms?: number;
    /** 'rule: multi-word' | 'fast_path' | 'llm' | 'fallback' | 'forced'. */
    source?: string;
  };
  filters?: {
    detected?: DirectSearchFilters;
    applied?: DirectSearchFilters;
    /**
     * Filters found in the query text but NOT applied, because the caller
     * cannot filter (Explorer). Absent when nothing was withheld.
     */
    locked?: DirectSearchFilters;
    /** The dimensions of `locked`: 'country' | 'university' | 'industry' | 'trl'. */
    locked_dims?: string[];
    /** Ready chip text for `locked`, e.g. ["Indian Institute of Technology Bombay", "TRL 5"]. */
    locked_labels?: string[];
  };
  expansion?: { expanded_query?: string; intent?: string; keywords?: string[]; ms?: number };
  timings?: {
    classify_ms?: number; filters_ms?: number; expand_ms?: number;
    retrieve_ms?: number; rescore_ms?: number; total_ms?: number;
  };
  candidates_considered?: number;
  /** True when this page was sliced from the server's 5 min result cache. */
  served_from_cache?: boolean;
  /**
   * True when the query took a simple route AND the admin setting
   * `deep_search_prompt` is on AND this was not already a forced run — i.e.
   * the UI should offer "Run deep search". Absent/false: offer nothing.
   */
  deep_search_offered?: boolean;
  /** True when the request carried force_route 'engine'. */
  deep_search_forced?: boolean;
  /**
   * Set when the query looks like keyboard mash AND the catalogue found
   * nothing. Both signals are required: either alone has a false-positive
   * mode, so `lexical` true with `flagged` false just means it looked odd and
   * matched anyway. `bypassed` is the "search anyway" re-run.
   */
  nonsense?: {
    flagged?: boolean; lexical?: boolean; bypassed?: boolean;
    reason?: string; signals?: string[];
  };
  /**
   * Why the server limited this caller, if it did: 'preview_only' when the
   * free-tier cap applied, 'filters_locked' when facets were ignored,
   * 'filters_detected_locked' when filters found in the query text were
   * withheld (see filters.locked), comma-joined. Absent means nothing was
   * withheld — do NOT infer a paywall from has_more_beyond_cap, which only
   * says the corpus holds more rows.
   */
  entitlement_note?: string | null;
  /**
   * True when the per-result score is an LLM relevance judgement. False (or
   * absent) on the quick routes, where the score is 1 - i/n — rank position,
   * not a verdict — and must not be shown as a percentage.
   */
  scores_are_relevance?: boolean;
  /**
   * "keyword" on a quick search the server scored by keyword match and sorted
   * by it (search_v2/keyword_score.py): each result's percentage is that
   * 0-100 match, and it is the number the Relevance pill shows.
   * "filter" when the query was only a filter ("IIT Bombay"): there is no
   * topic to score, so no pill is shown.
   */
  score_kind?: 'keyword' | 'filter' | null;
  /**
   * Set when the quick route found a misspelt word and searched the corrected
   * one instead ("batery" → "battery"). Absent when nothing was corrected.
   */
  typo_correction?: { from?: string; to?: string; changes?: [string, string][] };
  /** Per-result relevance cut applied on the deep route. */
  relevance_filter?: { min?: number; hidden?: number };
  settings?: Record<string, unknown>;
}

/**
 * The backend a response REALLY came from, as the string search history
 * stores and the admin User 360 "Searches" tab shows verbatim.
 *
 *   search_v2 → "v2:<engine>:<route>", the same label the server writes to
 *               search_query_logs ("v2:rag_pinecone:typesense",
 *               "v2:rag_pinecone:engine"); a forced deep search is
 *               "v2:<engine>:deep".
 *   legacy     → the endpoint's own `method` ("hybrid", "algolia", "native").
 *
 * Until this existed every history row said whatever backend literal the
 * page state carried ("algolia" for years, then "hybrid") regardless of
 * what actually ran — so the admin tab could not tell a Typesense hit from a
 * RAG run.
 */
export function searchMethodLabel(
  res: Pick<DirectSearchResponse, 'method' | 'meta'> | null | undefined,
  fallback: string,
): string {
  const meta = res?.meta;
  if (meta && typeof meta.engine === 'string' && meta.engine) {
    const route = meta.deep_search_forced
      ? 'deep'
      : (typeof meta.route === 'string' && meta.route ? meta.route : 'engine');
    return `v2:${meta.engine}:${route}`;
  }
  const m = res?.method;
  return typeof m === 'string' && m ? m : fallback;
}

export interface DirectSearchResponse {
  success: boolean;
  /** One of the DirectSearchBackend literals, or whatever v2 names its engine. */
  method: DirectSearchBackend | string;
  query: string;
  filters_applied: DirectSearchFilters;
  results: any[];
  total: number;
  total_uncapped?: number;
  has_more_beyond_cap?: boolean;
  page: number;
  nb_pages?: number;
  hits_per_page: number;
  facet_counts?: DirectSearchFacetCounts;
  processing_time: number;
  error?: string;
  /**
   * Filters the AI query-understanding layer extracted from the query text
   * (e.g. "iot for defence trl 6" -> { trl_min: 6, trl_max: 6 }). Same shape
   * as DirectSearchFilters; the UI reflects them into the filter panel.
   */
  ai_filters?: DirectSearchFilters;
  /**
   * search_v2: filters found in the query text but withheld because the
   * caller cannot filter (Explorer). Same shape as ai_filters, which is then
   * empty. The page offers the upgrade instead of ticking them.
   */
  ai_filters_locked?: DirectSearchFilters;
  /** Query text after the AI stripped filter phrases out of it. */
  ai_search_query?: string | null;
  /**
   * search_v2 quick route: the query as actually searched after a typo fix
   * ("batery cells" → "battery cells"). Absent when nothing was corrected.
   */
  corrected_query?: string | null;
  served_from_cache?: boolean;
  /** Present only when the response came from search_v2. */
  meta?: SearchV2Meta;
}

export interface ProfileFieldSpec {
  key: string;
  label: string;
  group: string;
  weight: number;
  kind: 'email' | 'text' | 'phone' | 'url' | 'select' | 'multiselect'
      | 'keywords' | 'nested_multiselect' | 'longtext' | 'longtext_list'
      // single_chip: one choice, rendered as chips, stored as a 1-element array.
      // dependent_checkboxes: options come from `options` keyed by the value of
      // `depends_on`, and each is {label, description}.
      | 'single_chip' | 'dependent_checkboxes';
  options?: string;
  max_select?: number;
  min_length?: number;
  max_items?: number;
  depends_on?: string;
  readonly?: boolean;
  help?: string;
  use_case?: string;
  required_free?: boolean;
  required_paid?: boolean;
}

export interface ProfileSpec {
  success: boolean;
  groups: { key: string; label: string; weight: number }[];
  fields: ProfileFieldSpec[];
  // Flat lists, nested lists, or (for dependent_checkboxes) groups of
  // {label, description} keyed by the parent field's value.
  options: Record<string,
    string[] | Record<string, string[]> | Record<string, { label: string; description?: string }[]>>;
  max_score: number;
  retired_fields: string[];
}

/** Every currency a plan can be priced in. Mirrors fx_rates.SUPPORTED.
 *  It was `'INR' | 'USD'`, which is what made three binary
 *  `currency === 'USD' ? '$' : '₹'` formatters look correct to the compiler
 *  while every other currency rendered as rupees. */
export type CurrencyCode =
  'INR' | 'USD' | 'EUR' | 'GBP' | 'AUD' | 'CAD' | 'SGD' | 'AED' | 'JPY' | 'CHF';

export interface BillingPlan {
  code: string;
  name: string;
  description: string;
  price_inr_paise: number;
  price_usd_cents: number;
  sort_order: number;
  /** False when the plan is withdrawn from sale. It still appears in a
   *  subscriber's summary so their current plan can be named, but it must not
   *  be offered for purchase. */
  is_active?: boolean;
  /** Feature name → what this plan gets ("Full access", "Included", …). */
  features?: Record<string, string>;
  /** What THIS user is charged, in minor units of `currency` (server-decided). */
  amount?: number;
  currency?: CurrencyCode;
  /** Which pricing region decided that currency. Lets an admin tell a
   *  mispriced region from a mis-detected country. */
  region?: string;
  /** Server-computed discounts, per flow — they differ, so never recompute. */
  one_time_off?: number;
  one_time_promo?: string | null;
  recurring_off?: number;
  recurring_promo?: string | null;
  recurring_cycles?: number;
}

export interface BillingSubscriptionInfo {
  plan_code: string;
  status: 'active' | 'cancelled' | string;
  auto_renew: boolean;
  source: string;
  is_trial: boolean;
  current_end: string | null;
  cancelled_at: string | null;
}

export interface BillingPaymentRow {
  date: string;
  amount_paise: number | null;
  currency: string;
  status: string;
  method: string;
  description: string;
  coupon: string | null;
  payment_id: string;
}

/** Explorer (free) | trial running | trial over, never paid | paid. */
/** 'voyager_assigned': a membership the team granted from the admin panel
 *  to someone who has never paid through Razorpay — same access as
 *  'voyager', told apart for the team. */
export type BillingState = 'explorer' | 'trial_active' | 'trial_lapsed' | 'voyager_assigned' | 'voyager';

/** What the trial form collects. All four are required by the server. */
export interface TrialForm {
  first_name: string;
  last_name: string;
  organization: string;
  designation: string;
}

/** The Trials tab's lifecycle filter. */
export type TrialSegment = 'all' | 'active' | 'lapsed' | 'converted' | 'cancelled';

export interface BillingTrialStatus {
  enabled: boolean;
  days: number;
  plan_code: string;
  /** How this customer can start one. 'card' is the card-free trial (the
   *  default: access is granted at once and lapses after `days`); 'mandate'
   *  is the legacy autopay trial an admin can switch back on, which sets up
   *  autopay and defers the first charge; 'none' when they cannot start one. */
  mode?: 'mandate' | 'card' | 'none';
  available: boolean;
  /** Why not, in the customer's words, when `available` is false. */
  reason: string;
  /** One trial per account, ever: true once any trial row exists, whatever
   *  became of it. The CTA greys out on this, with `reason` as the caption. */
  used?: boolean;
  used_reason?: 'active' | 'lapsed' | 'cancelled' | 'converted' | null;
}

export interface BillingTrialLifecycle {
  subscription_id?: string;
  /** = trial_start_timestamp: the welcome / ending-soon / lapsed mails are
   *  all keyed off it. */
  started_at: string | null;
  ends_at: string | null;
  lapsed_at: string | null;
  status: string;
  /** Whole days until `ends_at`, never negative. */
  days_left: number;
  /** The details snapshot taken at trial start. */
  form?: (TrialForm & { captured_at?: string }) | null;
}

export interface BillingSummary {
  success: boolean;
  plans: BillingPlan[];
  subscription: BillingSubscriptionInfo | null;
  payments: BillingPaymentRow[];
  payment_method: string;
  one_time_allowed: boolean;
  is_admin: boolean;
  key_id_present: boolean;
  /** Row order for the pricing comparison table. */
  feature_order: string[];
  /** Billing currency for this user, decided server-side from their country. */
  currency: CurrencyCode;
  country: string;
  /** False outside India — Razorpay auto-renew is INR-only on this account. */
  auto_renew_available: boolean;
  /** "Turn on auto-renewal" for a paid period with no renewal: the card is
   *  added now and first charged `amount` on `charge_on` (the day access
   *  ends). Null when it does not apply. */
  renewal?: { plan_code: string; plan_name: string; amount: number; currency: CurrencyCode;
              charge_on: string } | null;
  /** Live auto-applied campaign, if one is running. */
  promotion: {
    name: string; description: string;
    discount_type: string; discount_value: number;
    ends_at: string | null; intro_cycles: number; amount_off: number;
  } | null;
  trial: BillingTrialStatus;
  /** Where this account is in the trial lifecycle (Sep 2026 spec). A lapsed
   *  trial is a stored state of its own, not "explorer again": it has the
   *  same access, but the trial CTA must stay greyed for good. Absent from an
   *  older backend, so readers fall back to deriving it. */
  state?: BillingState;
  /** The trial row's dates and the details captured when it started; null
   *  when this account has never had one. */
  trial_lifecycle?: BillingTrialLifecycle | null;
  /** Coupon used on the payment that started the current subscription. */
  voucher: string | null;
  /** Options offered when a trial is cancelled, in display order, "Other"
   *  last. Server-authored so the admin table and the modal agree. */
  trial_cancel_reasons?: string[];
}

export interface DirectSearchPopularResponse {
  success: boolean;
  method: 'popular';
  results: any[];
  total: number;
  error?: string;
}

export interface DirectSearchHistoryEntry {
  id: string;
  user_id: string;
  query: string;
  filters: DirectSearchFilters;
  /**
   * Stored verbatim: a legacy literal ('algolia' | 'hybrid' | 'native') on
   * old rows, the real method ("v2:<engine>:<route>") on rows saved since
   * the frontend started reporting it.
   */
  backend: DirectSearchBackend | string;
  total: number;
  created_at: string | null;
}

/** A technology's university contacts: exactly the four fields
 *  GET /technologies/{id} used to return, same shapes. JSON columns may still
 *  arrive as strings, so readers keep parsing them defensively. */
export interface TechContacts {
  /** {name?, emails?: string[], ...} | null — the curated office. */
  tto_office_details: any;
  /** {name?, email?} | {} — the scraped licensing office. */
  tech_manager_default: any;
  common_email: string | null;
  /** [{name?, email?, contact_number?}] | {} — named managers. */
  tech_manager_details: any;
}

/** Where an account stands against its university-contact limit. `limit`
 *  null = unlimited; `window_label` is user-facing ("during your trial",
 *  "this month", ...). */
export interface ContactUsage {
  tier: 'trial' | 'paid' | 'free' | 'admin';
  state: string;
  window: string;
  window_label: string;
  used: number;
  limit: number | null;
  remaining: number | null;
  source: 'default' | 'account' | 'admin';
  default_limit: number | null;
  override: {
    tier: string; limit: number | null; note: string;
    set_by: string; set_at: string; applies_now: boolean;
  } | null;
  pending_request: { id: number; created_at: string; extra_requested: number | null } | null;
}

/** GET /technologies/{id}/contacts. `usage` is null only on the pre-limits
 *  fallback (older backend), where nothing is counted. */
export interface TechContactsResponse {
  allowed: boolean;
  counted?: boolean;
  reason?: 'limit_reached' | 'upgrade_required';
  contacts?: TechContacts;
  usage: ContactUsage | null;
}

export interface ContactRequestResponse {
  success: boolean;
  duplicate: boolean;
  request: { id: number; created_at: string; extra_requested: number | null };
  usage: ContactUsage;
}

/** The page a Contact University sheet or a Talk to Concierge click came
 *  from: the path only (a query string can carry a search or a token), within
 *  the server's 500-character cap. "" outside a browser. */
export function contactPagePath(): string {
  try {
    return typeof window !== 'undefined' ? window.location.pathname.slice(0, 500) : '';
  } catch {
    return '';
  }
}

// Export singleton instance
export const apiService = new ApiService();
